import * as THREE from 'three';
import { createSharedGLTFLoader } from '../render/SharedGLTFLoader';
import { createEngineCore, hueToCoreHdr, type EngineCoreHandle } from '../render/EngineCore';
import { createPlasmaBeamMaterial, type PlasmaBeamRuntimeMaterial } from '../render/PlasmaBeam';
import type { RendererMode } from '../render/Renderer';
import type { RacerState } from './Physics';
import { locateTrackPosition, type Track } from '../track/TrackLoader';
import { trackBankAtArc } from '../track/TrackElevation';
import { installAerialPerspectiveForObject } from '../render/AerialPerspective';
import { installValueCompositionForObject, tagValueBucketDeep } from '../render/ValueComposition';
import { DEFAULT_RENDER_FPS_CAP, FIXED_UPDATE_DT } from './FrameTiming';
import type { WallHit } from './Collision';

const HOVER_HEIGHT = 0.4;
const RACER_TARGET_LENGTH_METERS = 6.0;
const DEFAULT_RENDER_DT = 1 / DEFAULT_RENDER_FPS_CAP;
const FIXED_PHYSICS_DT = FIXED_UPDATE_DT;
const MODEL_LOAD_RETRY_DELAYS_MS = [120, 360];
/** Fraction of cable rest length removed during a slack envelope at strength=1. */
const COLLISION_CABLE_SLACK_DROP = 0.40;
/** Slack envelope duration in seconds at strength=1. */
const COLLISION_CABLE_SLACK_SECONDS = 0.35;

const racerModelCache = new Map<string, Promise<THREE.Object3D>>();
const rawModelCache = new Map<string, Promise<THREE.Object3D>>();

/**
 * Load the racer GLB once per URL and return the shared, fitted scene.
 * Subsequent callers receive the same Object3D — clone it, don't mutate it.
 */
export function loadRacerModel(url: string): Promise<THREE.Object3D> {
  const cached = racerModelCache.get(url);
  if (cached) return cached;
  const promise = loadGltfSceneWithRetry(url)
    .then((gltf) => {
      const model = gltf;
      fitToTargetLength(model, RACER_TARGET_LENGTH_METERS);
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      console.log('[racer] loaded glb size:', size.x.toFixed(2), size.y.toFixed(2), size.z.toFixed(2));
      return model;
    })
    .catch((err) => {
      racerModelCache.delete(url);
      throw err;
    });
  racerModelCache.set(url, promise);
  return promise;
}

/**
 * Returns a clone of the cached racer model. Geometry and materials are
 * shared with the cached original, so 16 ghost cars cost one parse + one
 * GPU upload.
 */
export async function cloneRacerModel(url: string): Promise<THREE.Object3D> {
  const cached = await loadRacerModel(url);
  return cached.clone(true);
}

function loadRawModel(url: string): Promise<THREE.Object3D> {
  const cached = rawModelCache.get(url);
  if (cached) return cached;
  const promise = loadGltfSceneWithRetry(url)
    .catch((err) => {
      rawModelCache.delete(url);
      throw err;
    });
  rawModelCache.set(url, promise);
  return promise;
}

async function cloneRawModel(url: string): Promise<THREE.Object3D> {
  const cached = await loadRawModel(url);
  return cached.clone(true);
}

async function loadGltfSceneWithRetry(url: string): Promise<THREE.Object3D> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MODEL_LOAD_RETRY_DELAYS_MS.length; attempt++) {
    try {
      const gltf = await createSharedGLTFLoader().loadAsync(url);
      return gltf.scene;
    } catch (err) {
      lastError = err;
      const delay = MODEL_LOAD_RETRY_DELAYS_MS[attempt];
      if (typeof delay !== 'number') break;
      await sleep(delay);
    }
  }
  throw lastError;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms));
}

export interface HoverMotionConfig {
  idleAmplitude: number;
  /** 0..1 multiplier on the speed-driven contribution to bounce/vibration/pitch. Default 1.0. */
  highSpeedDamping?: number;
  bounceAmplitude: number;
  vibrationAmplitude: number;
  pitchDeg: number;
  pulseScale: number;
}

export interface ScrapeEmitter {
  position: THREE.Vector3;
  intensity: number;
}

export interface RacerOptions {
  showPlaceholder?: boolean;
  rendererMode?: RendererMode;
}

export interface CollisionVisualTuning {
  collisionMinBounceSpeed: number;
  collisionEngineKick: number;
  collisionEngineSqueeze: number;
  collisionCockpitPunch: number;
}

export interface PodRacerAssemblyAssets {
  cockpitUrl: string;
  pilotUrl: string;
  engineLeftUrl: string;
  engineRightUrl: string;
}

export interface PodRacerRigTuning {
  modelScale: number;
  engineScale: number;
  engineSpread: number;
  engineHeight: number;
  engineForward: number;
  pilotScale: number;
  pilotY: number;
  pilotZ: number;
  cableSag: number;
  cockpitSocketX: number;
  cockpitSocketY: number;
  cockpitSocketZ: number;
  engineSocketX: number;
  engineSocketY: number;
  engineSocketZ: number;
  socketLaneGap: number;
  socketLaneRise: number;
  connectorScale: number;
  cableRadius: number;
  cableInsert: number;
  cockpitSocketDepth: number;
  engineSocketDepth: number;
  cockpitSocketYawDeg: number;
  cockpitSocketPitchDeg: number;
  engineSocketYawDeg: number;
  engineSocketPitchDeg: number;
  engineNozzleY: number;
  engineNozzleZ: number;
  engineNozzleScale: number;
  engineGlowHue: number;
  engineGlowIntensity: number;
  engineGlowRange: number;
  blueGlowHue: number;
  blueGlowIntensity: number;
  blueGlowRange: number;
  enginePlumeLength: number;
  enginePlumeRadius: number;
  enginePlumeOpacity: number;
  emitterOffsetX: number;
  emitterOffsetY: number;
  emitterOffsetZ: number;
  emitterScale: number;
  emitterIntensity: number;
  idleIntensity: number;
  coreHueDeg: number;
  outerHueDeg: number;
  plasmaIntensity: number;
  plasmaHueDeg: number;
  couplingEnabled: boolean;
  inertialLagEnabled: boolean;
  cableStiffness: number;
  cableDamping: number;
  strutStiffness: number;
  strutDamping: number;
  inertiaMass: number;
  restStiffness: number;
  engineDamping: number;
  cockpitReactionRoll: number;
  cockpitReactionPitch: number;
  cockpitWhipStrength: number;
  cockpitWhipStiffness: number;
  cockpitWhipDamping: number;
  cockpitWhipMaxOffset: number;
  engineYawGain: number;
  engineYawSmooth: number;
  cableSlackBoost: number;
  cableWeaveDamping: number;
}

interface EngineSpringState {
  group: THREE.Group;
  rest: THREE.Vector3;
  offset: THREE.Vector3;
  velocity: THREE.Vector3;
  cableForce: THREE.Vector3;
  cableRestLength: number;
  cableAltRestLength: number;
  seed: number;
  side: -1 | 1;
  /**
   * Transient cable slack envelope on impact. While > 0, the effective
   * cable rest length is reduced so the engine has room to plunge before
   * the cable snaps it back — that's the BeamNG trick that makes crashes
   * look like deformation rather than oscillation. Decays linearly toward
   * 0 over `cableSlackTotalSec` (typically ~350 ms).
   */
  cableSlackTimer: number;
  cableSlackTotalSec: number;
  /** Strength of the active slack envelope (0..1). */
  cableSlackStrength: number;
}

interface DynamicTube {
  mesh: THREE.Mesh;
  geometry: THREE.BufferGeometry;
  positions: Float32Array;
  normals: Float32Array;
  segments: number;
  radialSegments: number;
  radius: number;
}

interface PlasmaStrand {
  tube: DynamicTube;
  material: PlasmaBeamRuntimeMaterial;
  phase: number;
  lane: number;
  hueOffset: number;
  intensityScale: number;
}

interface PodRacerAssembly {
  root: THREE.Group;
  cockpit: THREE.Group;
  cockpitRest: THREE.Vector3;
  cockpitOffset: THREE.Vector3;
  cockpitVelocity: THREE.Vector3;
  pilot: THREE.Group;
  engineL: EngineSpringState;
  engineR: EngineSpringState;
  cockpitConnectors: THREE.Group[];
  engineConnectors: THREE.Group[];
  cableL: DynamicTube;
  cableR: DynamicTube;
  cableLAlt: DynamicTube;
  cableRAlt: DynamicTube;
  plasmaStrands: PlasmaStrand[];
  exhaustL: THREE.Mesh;
  exhaustR: THREE.Mesh;
  coreL: EngineCoreHandle;
  coreR: EngineCoreHandle;
  lightL: THREE.PointLight;
  lightR: THREE.PointLight;
  blueLightL: THREE.PointLight;
  blueLightR: THREE.PointLight;
  nozzleL: THREE.Object3D;
  nozzleR: THREE.Object3D;
  previousForwardSpeed: number;
  prevCockpitVelocity: THREE.Vector3;
  strutRestLength: number;
  targetEngineYawL: number;
  targetEngineYawR: number;
}

const DEFAULT_POD_RACER_RIG_TUNING: PodRacerRigTuning = {
  modelScale: 1.2,
  engineScale: 0.92,
  engineSpread: 1.14,
  engineHeight: 0.45,
  engineForward: 1.24,
  pilotScale: 1.32,
  pilotY: 0.55,
  pilotZ: -1.12,
  cableSag: 0.1,
  cockpitSocketX: 0.35,
  cockpitSocketY: 0.57,
  cockpitSocketZ: -0.62,
  engineSocketX: 0.52,
  engineSocketY: 0.22,
  engineSocketZ: -0.5,
  socketLaneGap: 0.05,
  socketLaneRise: 0.14,
  connectorScale: 0.55,
  cableRadius: 0.023,
  cableInsert: 0.08,
  cockpitSocketDepth: 0.02,
  engineSocketDepth: 0.14,
  cockpitSocketYawDeg: 51,
  cockpitSocketPitchDeg: -4,
  engineSocketYawDeg: -31,
  engineSocketPitchDeg: -25,
  engineNozzleY: 0.35,
  engineNozzleZ: -0.82,
  engineNozzleScale: 0.22,
  engineGlowHue: 28,
  engineGlowIntensity: 1.3,
  engineGlowRange: 8,
  blueGlowHue: 183,
  blueGlowIntensity: 1,
  blueGlowRange: 4.5,
  enginePlumeLength: 1.9,
  enginePlumeRadius: 0.22,
  enginePlumeOpacity: 0.5,
  emitterOffsetX: 0.01,
  emitterOffsetY: 0.03,
  emitterOffsetZ: 0.1,
  emitterScale: 0.7,
  emitterIntensity: 1.23,
  idleIntensity: 0.245,
  coreHueDeg: 213,
  outerHueDeg: 217,
  plasmaIntensity: 2.59,
  plasmaHueDeg: 283,
  couplingEnabled: true,
  inertialLagEnabled: true,
  cableStiffness: 65,
  cableDamping: 6,
  strutStiffness: 20,
  strutDamping: 3,
  inertiaMass: 0.115,
  restStiffness: 8,
  engineDamping: 2.5,
  cockpitReactionRoll: 0.0205,
  cockpitReactionPitch: 0.011,
  cockpitWhipStrength: 0.0165,
  cockpitWhipStiffness: 58,
  cockpitWhipDamping: 12,
  cockpitWhipMaxOffset: 0.51,
  engineYawGain: 0,
  engineYawSmooth: 3.8,
  cableSlackBoost: 1.4,
  cableWeaveDamping: 0.52,
};

const POD_PLASMA_STRANDS = [
  { lane: 0, radius: 0.018, radialSegments: 6, hueOffset: 0, phase: 0.3, intensityScale: 0.82 },
  { lane: -1, radius: 0.012, radialSegments: 5, hueOffset: -22, phase: 1.7, intensityScale: 0.48 },
  { lane: 1, radius: 0.012, radialSegments: 5, hueOffset: 18, phase: 3.35, intensityScale: 0.48 },
  { lane: -0.46, radius: 0.009, radialSegments: 5, hueOffset: 38, phase: 5.05, intensityScale: 0.3 },
  { lane: 0.46, radius: 0.009, radialSegments: 5, hueOffset: -36, phase: 6.55, intensityScale: 0.3 },
] as const;

/**
 * Owns the racer's THREE.Group and physics state.
 * The Group's children may later be swapped from [wholeModel] to
 * [cockpit, engineL, engineR, tetherL, tetherR] without touching physics.
 *
 * `modelHolder` is a child group whose rotation re-orients the model so its
 * "forward" lines up with local +Z without touching physics.
 */
export class Racer {
  readonly group = new THREE.Group();
  /** Rotation around Y, degrees. 0 = model already faces +Z. */
  modelYawDeg = 0;
  /** Uniform scale multiplier on top of fitToTargetLength. Source-of-truth lives in podRigTuning.modelScale; applyPodRigTuning() syncs this field for the visual update path. */
  modelScale = 1.2;
  /** Visual-only body roll while turning, in degrees. */
  maxBankDeg = 52;
  bankResponsiveness = 15;
  /**
   * Per-engine differential thrust drives an additional body roll: the
   * engine working harder produces more vertical reaction force so its
   * side rises and the body rolls toward the slacker side. This is what
   * gives stick-mode the visceral "the harder I push the more it tilts"
   * feel even at zero speed. Combined with the steer-driven bank above.
   */
  thrustBankDeg = 8;
  /** Ground-sample asymmetry → roll. The two engines sample the hover
   *  noise field at offset positions (and, in the future, the actual
   *  track surface via raycasts), so uneven ground naturally rolls the
   *  body. On a flat track this gives subtle micro-rocking at idle. */
  groundRollDeg = 3;
  /**
   * Visual-only longitudinal pitch driven by forward acceleration:
   * squat (nose-up) under throttle, dive (nose-down) under braking/coast.
   * Brake dive is heavily exaggerated — the pod pitches forward into a
   * hairpin like a MotoGP stoppie, which sells the deceleration far
   * harder than a symmetric squat/dive ever could.
   */
  maxAccelPitchUpDeg = 10.0;
  maxAccelPitchDownDeg = 16.0;
  /** Acceleration (m/s^2) that maps to the side's max pitch angle. */
  pitchAccelScale = 38;
  pitchResponsiveness = 14;
  /** Visual hover/suspension tuning. Surface roughness is assigned by the track state. */
  trackSurfaceRoughness = 0.18;
  offTrackSurfaceRoughness = 0.3;
  surfaceRoughness = this.trackSurfaceRoughness;
  hoverIdleAmplitude = 0.01;
  hoverBounceAmplitude = 0.018;
  hoverVibrationAmplitude = 0.0025;
  hoverPitchDeg = 0.35;
  /**
   * Track-following suspension. The chassis lags vertically when the ground
   * rises/falls underneath it: a critically-damped 1D spring driven by the
   * ground's vertical velocity. Travel is the meters-of-Y-offset per (m/s)
   * of ground velocity; response is the spring omega (rad/s) — lower = slower
   * and smoother (more visible "weight on suspension"), higher = stiffer
   * (chassis sticks closer to the ground line). This is independent of
   * `hoverBounceAmplitude`, which is the always-on time-driven bob.
   */
  trackSuspensionTravel = 0.18;
  trackSuspensionResponse = 5;
  /** Hard cap on the suspension Y offset to prevent runaway travel on cliffs. */
  trackSuspensionMaxOffset = 0.18;
  /**
   * Hovercraft buoyancy: a continuous, low-frequency vertical drift that
   * reads as the craft floating on a cushion of air. Amplitude is meters
   * of bob; frequency is Hz (cycles per second). Slower than the existing
   * `hoverBounce` (which is a fast suspension chatter) — buoyancy is the
   * 2-4 second "I'm hovering on a fluid" rhythm. Set amplitude to 0 to
   * disable.
   */
  hoverBuoyancyAmplitude = 0.022;
  hoverBuoyancyHz = 0.42;
  /**
   * Speed-coupled lift (meters at top speed). Like ground effect on a real
   * hovercraft — the faster you go, the more lift you generate, so the
   * resting hover height rises a few cm. Smoothed via the same speed
   * signal as the bank/hover system. Zero = no lift, just static hover.
   */
  hoverSpeedLift = 0.06;
  /**
   * Time constant (seconds) for the speed-lift to ease in/out as speed
   * changes. Higher = laggier (more weighty); lower = snappier.
   */
  hoverSpeedLiftResponse = 0.6;
  /**
   * Centrifugal feedback: when the cockpit whips outward (lateral lag from
   * a sharp turn or a hard yank), this gain feeds the offset back into
   * the racer's lateral velocity. Net effect is the pod's mass dragging
   * the body further around — a hard turn feels like the pod swings the
   * craft. 0 = visual whip only (legacy); ~6–12 is a noticeable but
   * controllable centrifugal pull. Set per-fixed-step as `velocity += RIGHT *
   * cockpitOffset.x * gain * dt`. Sign matches the offset: pod whips
   * outward in a turn → body gets pulled further outward → wider arc.
   * Applied from the fixed update path, not render(), so physics remains
   * tied to the game-loop clock.
   */
  cockpitWhipPhysicsGain = 0;
  /**
   * Hovercraft gravity (m/s²) applied to vertical velocity while airborne.
   * Lower = floatier, longer hangtime; higher = arcade-snappy fall. Real
   * gravity is 9.81 but hover racers want a heavier-feeling fall so the
   * craft doesn't moonwalk through jumps. Wipeout/F-Zero tune this in the
   * 18–32 range; default 22 is a good "weighty hover" baseline.
   */
  gravityY = 22;
  /**
   * Multiplier converting landing impact speed (m/s of downward velocity
   * at touchdown) into a negative kick on `suspensionVelocity` — the
   * chassis compresses, then the existing critically-damped spring
   * recovers it. Higher = harder slam read on landings.
   */
  landingCompressionGain = 0.45;
  /**
   * Minimum downward speed at touchdown that counts as a "real" landing
   * worth kicking the suspension. Below this we just clean-snap to the
   * ground without a visual thump (riding crests at speed shouldn't
   * trigger phantom landings).
   */
  landingImpactThreshold = 1.5;
  /**
   * 0..1 multiplier on the speed-driven contribution to hover bounce/vibration/pitch.
   * 1.0 = full speed-scaled wobble (legacy feel); 0.0 = idle-and-roughness only.
   * Lower this when the pod feels "bouncy" at top speed without killing low-speed
   * suspension feel. Roughness contribution (off-track / kerbs) is unaffected.
   */
  hoverHighSpeedDamping = 1.0;
  enginePulseScale = 0.002;
  scrapeBankThresholdDeg = 24;

  readonly state: RacerState = {
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    heading: 0,
    yawVelocity: 0,
    driftCharge: 0,
    boostTimer: 0,
    isDrifting: false,
    verticalVelocity: 0,
    airborne: false,
  };

  private readonly modelHolder = new THREE.Group();
  private podAssembly: PodRacerAssembly | null = null;
  private podRigTuning: PodRacerRigTuning = { ...DEFAULT_POD_RACER_RIG_TUNING };
  private podPreviewThrottle = 0;
  private podRuntimeGlowScale = 1;
  /** Smoothed 0..1 ramp: 0 = idle pilot flame, 1 = full plume. Hold thrust to ramp up; releases ease back. */
  private throttleHeldTime = 0;
  private podSelfLightingEnabled = true;
  private visualModelEnabled = true;
  private placeholderModelUseCount = 0;
  private readonly prevPos = new THREE.Vector3();
  private readonly prevHeading = { value: 0 };
  private visualBank = 0;
  private bankVelocity = 0;
  private bankSignal = 0;
  private bankLoad = 0;
  private track: Track | null = null;
  private surfaceBank = 0;
  // Latest per-engine thrust in [-1,+1]. Set by main.ts each frame from
  // the InputManager so the bank spring can react. undefined = legacy
  // (steer-only) input mode; the differential bank contribution drops
  // out so keyboard players see no behavioral change.
  private engineThrustL: number | undefined = undefined;
  private engineThrustR: number | undefined = undefined;
  private visualPitch = 0;
  private pitchVelocity = 0;
  private prevForwardSpeed = 0;
  private readonly prevVelocity = new THREE.Vector3();
  private suspensionY = 0;
  private suspensionVelocity = 0;
  private prevGroundY: number | null = null;
  private smoothedSpeedLift = 0;
  private visualSurfaceRoughness = this.trackSurfaceRoughness;
  private visualClockSeconds = 0;
  private readonly engineNozzleOffsets = [
    new THREE.Vector3(-1.45, 0.55, -0.15),
    new THREE.Vector3(1.45, 0.55, -0.15),
  ];

  /** A small bright triangle pointing in physics-forward (+Z). Toggle via `showForwardArrow`. */
  showForwardArrow = false;
  private readonly forwardArrow: THREE.Mesh;
  private readonly rendererMode: RendererMode;

  constructor(opts: RacerOptions = {}) {
    this.rendererMode = opts.rendererMode ?? 'webgl2';
    this.group.add(this.modelHolder);
    if (opts.showPlaceholder === false) {
      this.modelHolder.visible = false;
    } else {
      this.usePlaceholderModel(true);
    }

    const arrowGeom = new THREE.ConeGeometry(0.6, 1.6, 4);
    arrowGeom.rotateX(Math.PI / 2);
    const arrowMat = new THREE.MeshBasicMaterial({ color: 0xffe600 });
    this.forwardArrow = new THREE.Mesh(arrowGeom, arrowMat);
    this.forwardArrow.position.set(0, 1.6, 2.2);
    this.group.add(this.forwardArrow);

    this.configureModelForShadows(this.modelHolder);
  }

  getPlaceholderModelUseCount(): number {
    return this.placeholderModelUseCount;
  }

  setVisualModelEnabled(enabled: boolean): void {
    this.visualModelEnabled = enabled;
    this.modelHolder.visible = enabled && this.modelHolder.children.length > 0;
    this.forwardArrow.visible = enabled && this.showForwardArrow;
  }

  isVisualModelEnabled(): boolean {
    return this.visualModelEnabled;
  }

  /** Try to load `racer.glb`; on failure, keep the placeholder. */
  async load(url: string): Promise<void> {
    try {
      const model = await cloneRacerModel(url);
      this.modelHolder.clear();
      this.podAssembly = null;
      this.modelHolder.add(model);
      this.modelHolder.visible = this.visualModelEnabled;
      this.configureModelForShadows(model);
      tagValueBucketDeep(model, 'fg');
      installAerialPerspectiveForObject(model);
      installValueCompositionForObject(model, 'fg');
    } catch (err) {
      this.modelHolder.visible = this.visualModelEnabled;
      console.warn('[racer] failed to load glb, using placeholder:', err);
    }
  }

  async loadPodRacerAssembly(assets: PodRacerAssemblyAssets): Promise<void> {
    const [cockpitModel, pilotModel, engineLModel, engineRModel] = await Promise.all([
      cloneRawModel(assets.cockpitUrl),
      cloneRawModel(assets.pilotUrl),
      cloneRawModel(assets.engineLeftUrl),
      cloneRawModel(assets.engineRightUrl),
    ]);

    normalizePart(cockpitModel, 1.9);
    normalizePart(pilotModel, 0.66);
    normalizePart(engineLModel, 1.78);
    normalizePart(engineRModel, 1.78);
    engineRModel.rotation.y = Math.PI;

    const root = new THREE.Group();
    root.name = 'pod-racer-assembly';
    root.userData.podAssemblyRoot = true;

    const cockpit = new THREE.Group();
    cockpit.name = 'cockpit';
    cockpit.position.set(0, 0.42, -1.08);
    cockpit.add(cockpitModel);
    root.add(cockpit);

    const pilot = new THREE.Group();
    pilot.name = 'racer-pilot';
    pilot.add(pilotModel);
    root.add(pilot);

    const engineLGroup = new THREE.Group();
    engineLGroup.name = 'engineL';
    engineLGroup.add(engineLModel);
    root.add(engineLGroup);

    const engineRGroup = new THREE.Group();
    engineRGroup.name = 'engineR';
    engineRGroup.add(engineRModel);
    root.add(engineRGroup);

    const connectorTexture = makeProceduralDetailTexture(0x2a211c, 0x5b3b2d);
    const cableTexture = makeProceduralDetailTexture(0x27211f, 0x5a453c);
    const connectorMat = new THREE.MeshStandardMaterial({
      color: 0x2a211c,
      map: connectorTexture ?? undefined,
      roughness: 0.68,
      metalness: 0.65,
    });
    const cockpitConnectors: THREE.Group[] = [];
    const engineConnectors: THREE.Group[] = [];
    for (const side of [-1, 1] as const) {
      const engineGroup = side < 0 ? engineLGroup : engineRGroup;
      for (const lane of [0, 1]) {
        const cockpitConnector = makeCableConnector(`cockpitConnector${side < 0 ? 'L' : 'R'}${lane}`, connectorMat, side);
        const engineConnector = makeCableConnector(`engineConnector${side < 0 ? 'L' : 'R'}${lane}`, connectorMat, side);
        cockpitConnectors.push(cockpitConnector);
        engineConnectors.push(engineConnector);
        root.add(cockpitConnector);
        engineGroup.add(engineConnector);
      }
    }

    const cableMat = new THREE.MeshStandardMaterial({
      color: 0x3d3430,
      map: cableTexture ?? undefined,
      roughness: 0.72,
      metalness: 0.18,
    });
    const cableL = createDynamicTube('cableL', 0.032, 12, 7, cableMat);
    const cableR = createDynamicTube('cableR', 0.032, 12, 7, cableMat);
    const cableLAlt = createDynamicTube('cableLAlt', 0.026, 12, 6, cableMat);
    const cableRAlt = createDynamicTube('cableRAlt', 0.026, 12, 6, cableMat);
    root.add(cableL.mesh, cableR.mesh, cableLAlt.mesh, cableRAlt.mesh);

    const plasmaStrands = POD_PLASMA_STRANDS.map((config, index): PlasmaStrand => {
      const material = createPlasmaBeamMaterial(
        this.rendererMode,
        this.podRigTuning.plasmaHueDeg + config.hueOffset,
        this.podRigTuning.plasmaIntensity * config.intensityScale,
      );
      const tube = createDynamicTube(`plasma-strand-${index}`, config.radius, 28, config.radialSegments, material);
      root.add(tube.mesh);
      return {
        tube,
        material,
        phase: config.phase,
        lane: config.lane,
        hueOffset: config.hueOffset,
        intensityScale: config.intensityScale,
      };
    });

    const glowDiscGeom = new THREE.CircleGeometry(1, 32);
    const exhaustMat = new THREE.MeshBasicMaterial({
      color: 0xffd29a,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const exhaustL = new THREE.Mesh(glowDiscGeom, exhaustMat);
    const exhaustR = new THREE.Mesh(glowDiscGeom, exhaustMat.clone());
    exhaustL.name = 'exhaustGlowL';
    exhaustR.name = 'exhaustGlowR';
    engineLGroup.add(exhaustL);
    engineRGroup.add(exhaustR);

    const coreL = createEngineCore({ length: 1.6, radius: 0.32, rendererMode: this.rendererMode });
    const coreR = createEngineCore({ length: 1.6, radius: 0.32, rendererMode: this.rendererMode });
    coreL.mesh.name = 'engineCoreL';
    coreR.mesh.name = 'engineCoreR';
    engineLGroup.add(coreL.mesh);
    engineRGroup.add(coreR.mesh);

    const lightL = new THREE.PointLight(0xff7a22, 2.2, 8, 2);
    const lightR = new THREE.PointLight(0xff7a22, 2.2, 8, 2);
    const blueLightL = new THREE.PointLight(0x68e8ff, 0.9, 4.5, 2);
    const blueLightR = new THREE.PointLight(0x68e8ff, 0.9, 4.5, 2);
    lightL.name = 'engineGlowLightL';
    lightR.name = 'engineGlowLightR';
    blueLightL.name = 'engineBlueGlowLightL';
    blueLightR.name = 'engineBlueGlowLightR';
    lightL.castShadow = false;
    lightR.castShadow = false;
    blueLightL.castShadow = false;
    blueLightR.castShadow = false;
    engineLGroup.add(blueLightL);
    engineRGroup.add(blueLightR);

    const nozzleL = new THREE.Object3D();
    const nozzleR = new THREE.Object3D();
    nozzleL.name = 'nozzleL';
    nozzleR.name = 'nozzleR';
    engineLGroup.add(nozzleL);
    engineRGroup.add(nozzleR);

    this.modelHolder.clear();
    this.podAssembly = {
      root,
      cockpit,
      cockpitRest: cockpit.position.clone(),
      cockpitOffset: new THREE.Vector3(),
      cockpitVelocity: new THREE.Vector3(),
      pilot,
      cockpitConnectors,
      engineConnectors,
      engineL: {
        group: engineLGroup,
        rest: engineLGroup.position.clone(),
        offset: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        cableForce: new THREE.Vector3(),
        cableRestLength: 1,
        cableAltRestLength: 1,
        seed: 1.7,
        side: -1,
        cableSlackTimer: 0,
        cableSlackTotalSec: 0,
        cableSlackStrength: 0,
      },
      engineR: {
        group: engineRGroup,
        rest: engineRGroup.position.clone(),
        offset: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        cableForce: new THREE.Vector3(),
        cableRestLength: 1,
        cableAltRestLength: 1,
        seed: 4.3,
        side: 1,
        cableSlackTimer: 0,
        cableSlackTotalSec: 0,
        cableSlackStrength: 0,
      },
      cableL,
      cableR,
      cableLAlt,
      cableRAlt,
      plasmaStrands,
      exhaustL,
      exhaustR,
      coreL,
      coreR,
      lightL,
      lightR,
      blueLightL,
      blueLightR,
      nozzleL,
      nozzleR,
      previousForwardSpeed: 0,
      prevCockpitVelocity: new THREE.Vector3(),
      strutRestLength: 1,
      targetEngineYawL: 0,
      targetEngineYawR: 0,
    };
    this.applyPodRigTuning();
    this.modelHolder.add(root);
    this.modelHolder.visible = this.visualModelEnabled;
    this.configureModelForShadows(root);
    tagValueBucketDeep(root, 'fg');
    try {
      installAerialPerspectiveForObject(root);
    } catch (err) {
      console.warn('[racer] failed to install aerial perspective on pod assembly:', err);
    }
    try {
      installValueCompositionForObject(root, 'fg');
    } catch (err) {
      console.warn('[racer] failed to install value composition on pod assembly:', err);
    }
    exhaustL.castShadow = false;
    exhaustR.castShadow = false;
    coreL.mesh.castShadow = false;
    coreR.mesh.castShadow = false;
    this.updatePodAssembly(this.visualClockSeconds, 0);
  }

  setPodRacerRigTuning(tuning: Partial<PodRacerRigTuning>): void {
    this.podRigTuning = { ...this.podRigTuning, ...tuning };
    this.applyPodRigTuning();
    this.updatePodAssembly(this.visualClockSeconds, 0);
  }

  getPodRacerRigTuning(): PodRacerRigTuning {
    return { ...this.podRigTuning };
  }

  setPodRacerPreviewThrottle(throttle: number): void {
    this.podPreviewThrottle = THREE.MathUtils.clamp(throttle, 0, 1);
  }

  setPodRacerRuntimeGlowScale(scale: number): void {
    this.podRuntimeGlowScale = THREE.MathUtils.clamp(scale, 0, 1.5);
  }

  setPodRacerSelfLightingEnabled(enabled: boolean): void {
    this.podSelfLightingEnabled = enabled;
    this.applyPodSelfLightingVisibility();
  }

  usePlaceholderModel(visible = true): void {
    this.placeholderModelUseCount += 1;
    this.modelHolder.clear();
    this.podAssembly = null;
    const placeholder = this.makePlaceholderModel();
    this.modelHolder.add(placeholder);
    this.modelHolder.visible = visible && this.visualModelEnabled;
    this.configureModelForShadows(this.modelHolder);
    tagValueBucketDeep(placeholder, 'fg');
    installAerialPerspectiveForObject(placeholder);
    installValueCompositionForObject(placeholder, 'fg');
  }

  /**
   * Bind the live track so render() can sample the curvature-driven surface
   * bank and roll the pod with the road. Optional — without it the pod still
   * renders correctly, just upright in world frame.
   */
  setTrack(track: Track): void {
    this.track = track;
  }

  setStart(x: number, z: number, heading: number): void {
    this.state.position.set(x, 0, z);
    this.state.velocity.set(0, 0, 0);
    this.state.heading = heading;
    this.state.yawVelocity = 0;
    this.state.driftCharge = 0;
    this.state.boostTimer = 0;
    this.state.isDrifting = false;
    this.prevPos.copy(this.state.position);
    this.prevHeading.value = heading;
    this.visualBank = 0;
    this.bankVelocity = 0;
    this.bankSignal = 0;
    this.bankLoad = 0;
    this.surfaceBank = 0;
    this.visualPitch = 0;
    this.pitchVelocity = 0;
    this.prevForwardSpeed = 0;
    this.prevVelocity.copy(this.state.velocity);
    this.suspensionY = 0;
    this.suspensionVelocity = 0;
    this.prevGroundY = null;
    this.smoothedSpeedLift = 0;
    this.visualClockSeconds = 0;
    this.state.verticalVelocity = 0;
    this.state.airborne = false;
    this.visualSurfaceRoughness = this.surfaceRoughness;
    this.group.position.copy(this.state.position);
    this.group.rotation.set(0, heading, 0);
    this.podAssembly?.prevCockpitVelocity.copy(this.state.velocity);
  }

  /** Visual roll (radians). Readonly snapshot for the ghost recorder. */
  getVisualBank(): number {
    return this.visualBank;
  }

  /** Feed the latest per-engine thrust (after InputManager smoothing) so
   *  the visual bank can react to differential thrust. Pass undefined to
   *  fall back to legacy steer-driven bank only. */
  setEngineThrust(left: number | undefined, right: number | undefined): void {
    this.engineThrustL = left;
    this.engineThrustR = right;
  }

  /**
   * Feed hard wall contacts into the visual spring rig. The gameplay body has
   * already been resolved by Collision.ts; this is just a diegetic reaction so
   * the engines and cockpit look like they absorbed the impulse.
   */
  applyCollisionVisualImpulse(hit: WallHit, tuning: CollisionVisualTuning): void {
    if (!this.podAssembly || !hit.hit) return;
    const minSpeed = Math.max(0, tuning.collisionMinBounceSpeed);
    const impact01 = THREE.MathUtils.clamp(
      (hit.impactSpeed - minSpeed) / Math.max(1, minSpeed * 5),
      0,
      1,
    );
    if (impact01 <= 0) return;

    const heading = this.state.heading;
    collisionImpulseWorld.set(-hit.nx, 0, -hit.nz);
    const len = Math.hypot(collisionImpulseWorld.x, collisionImpulseWorld.z);
    if (len <= 1e-6) return;
    collisionImpulseWorld.multiplyScalar(1 / len);
    worldVectorToAssemblyLocal(collisionImpulseWorld, Math.sin(heading), Math.cos(heading), collisionImpulseLocal);

    const kick = Math.max(0, tuning.collisionEngineKick) * impact01;
    const squeeze = Math.max(0, tuning.collisionEngineSqueeze) * impact01 * (0.4 + Math.abs(collisionImpulseLocal.z) * 0.6);
    const cockpitPunch = Math.max(0, tuning.collisionCockpitPunch) * impact01;

    // Asymmetric three-body routing: the engine on the wall side absorbs
    // the bulk of the impulse, the far-side engine kicks lightly. Wall side
    // is the engine whose `side` matches -sign(collisionImpulseLocal.x):
    // collisionImpulseLocal points AWAY from the wall in pod-local space,
    // so a positive x means the wall is on the left (engine.side = -1).
    // Sideness fades to zero on near-symmetric head-on hits, so frontal
    // crashes still kick both engines roughly evenly.
    const sideness = THREE.MathUtils.clamp(collisionImpulseLocal.x, -1, 1);
    const wallSign = -Math.sign(sideness); // engine.side that is on the wall
    const sideMag = Math.abs(sideness);
    const lOnWall = this.podAssembly.engineL.side === wallSign && sideMag > 0.05;
    const rOnWall = this.podAssembly.engineR.side === wallSign && sideMag > 0.05;
    const lWeight = lOnWall ? 1 + sideMag * 0.9 : 1 - sideMag * 0.55;
    const rWeight = rOnWall ? 1 + sideMag * 0.9 : 1 - sideMag * 0.55;

    // Cable slack envelope on the wall-side engine: temporarily reduces the
    // cable's effective rest length so the engine can plunge toward the pod
    // and stay compressed, then ramps back to nominal as slack decays. The
    // far-side engine gets a smaller slack pulse so the whole rig "settles
    // in" briefly. This is the BeamNG beamDeform trick — visible compression
    // comes from rest-length modulation, not bigger spring oscillation.
    const slackStrength = THREE.MathUtils.clamp(impact01 * (0.6 + sideMag * 0.6), 0, 1);
    if (slackStrength > 0.05) {
      const wallEngine = lOnWall ? this.podAssembly.engineL : (rOnWall ? this.podAssembly.engineR : null);
      const farEngine = lOnWall ? this.podAssembly.engineR : (rOnWall ? this.podAssembly.engineL : null);
      if (wallEngine !== null) {
        wallEngine.cableSlackStrength = Math.max(wallEngine.cableSlackStrength, slackStrength);
        wallEngine.cableSlackTotalSec = COLLISION_CABLE_SLACK_SECONDS;
        wallEngine.cableSlackTimer = COLLISION_CABLE_SLACK_SECONDS;
      }
      if (farEngine !== null) {
        const farStrength = slackStrength * 0.45;
        farEngine.cableSlackStrength = Math.max(farEngine.cableSlackStrength, farStrength);
        farEngine.cableSlackTotalSec = COLLISION_CABLE_SLACK_SECONDS;
        farEngine.cableSlackTimer = COLLISION_CABLE_SLACK_SECONDS;
      } else {
        // Head-on hit (no wall side): both engines get a moderate slack pulse.
        const symStrength = slackStrength * 0.7;
        this.podAssembly.engineL.cableSlackStrength = Math.max(this.podAssembly.engineL.cableSlackStrength, symStrength);
        this.podAssembly.engineL.cableSlackTotalSec = COLLISION_CABLE_SLACK_SECONDS;
        this.podAssembly.engineL.cableSlackTimer = COLLISION_CABLE_SLACK_SECONDS;
        this.podAssembly.engineR.cableSlackStrength = Math.max(this.podAssembly.engineR.cableSlackStrength, symStrength);
        this.podAssembly.engineR.cableSlackTotalSec = COLLISION_CABLE_SLACK_SECONDS;
        this.podAssembly.engineR.cableSlackTimer = COLLISION_CABLE_SLACK_SECONDS;
      }
    }

    this.podAssembly.engineL.velocity.x += collisionImpulseLocal.x * kick * 0.42 * lWeight + squeeze;
    this.podAssembly.engineR.velocity.x += collisionImpulseLocal.x * kick * 0.42 * rWeight - squeeze;
    this.podAssembly.engineL.velocity.z += collisionImpulseLocal.z * kick * lWeight;
    this.podAssembly.engineR.velocity.z += collisionImpulseLocal.z * kick * rWeight;
    this.podAssembly.engineL.velocity.y += Math.abs(collisionImpulseLocal.z) * kick * 0.14 * lWeight;
    this.podAssembly.engineR.velocity.y += Math.abs(collisionImpulseLocal.z) * kick * 0.14 * rWeight;
    this.podAssembly.engineL.velocity.clampLength(0, 8);
    this.podAssembly.engineR.velocity.clampLength(0, 8);

    // Cockpit punch picks up an angular yank from the asymmetry: side hits
    // also rotate the pod's visual swing because the wall-side engine
    // decelerates harder than the far-side engine.
    this.podAssembly.cockpitVelocity.x -= collisionImpulseLocal.x * cockpitPunch;
    this.podAssembly.cockpitVelocity.z -= collisionImpulseLocal.z * cockpitPunch * 0.6;
    this.pitchVelocity += -collisionImpulseLocal.z * cockpitPunch;
  }

  /** Visual longitudinal pitch (radians). */
  getVisualPitch(): number {
    return this.visualPitch;
  }

  /** Snapshot pre-step state so render() can interpolate. */
  beginStep(): void {
    this.prevPos.copy(this.state.position);
    this.prevHeading.value = this.state.heading;
    this.prevVelocity.copy(this.state.velocity);
    const fwdX = Math.sin(this.state.heading);
    const fwdZ = Math.cos(this.state.heading);
    this.prevForwardSpeed = this.state.velocity.x * fwdX + this.state.velocity.z * fwdZ;
  }

  /**
   * Render at interpolated transform between previous and current physics
   * step. `frameDt` is the real wallclock seconds since the last render —
   * pass it through so the bank/pitch springs evolve at a refresh-rate
   * independent rate. `clockTimeSeconds` should come from the game loop's
   * render clock; when omitted, Racer advances an internal fallback clock so
   * debug tools and tests stay deterministic without reading wall time.
   */
  render(alpha: number, frameDt: number = DEFAULT_RENDER_DT, clockTimeSeconds?: number): void {
    this.group.position.lerpVectors(this.prevPos, this.state.position, alpha);
    const groundY = THREE.MathUtils.lerp(this.prevPos.y, this.state.position.y, alpha);
    // Clamp dt: 0 stalls the spring, and unbounded values from a long pause
    // would teleport it. Cap at ~250ms which is also GameLoop's frameDt cap.
    const dt = Math.min(Math.max(frameDt, 0), 0.25);
    if (typeof clockTimeSeconds === 'number' && Number.isFinite(clockTimeSeconds)) {
      this.visualClockSeconds = clockTimeSeconds;
    } else {
      this.visualClockSeconds += dt;
    }
    const timeSeconds = this.visualClockSeconds;
    this.visualSurfaceRoughness += (
      THREE.MathUtils.clamp(this.surfaceRoughness, 0, 1) - this.visualSurfaceRoughness
    ) * (1 - Math.exp(-4.8 * dt));
    const hover = __test__computeHoverMotion(
      timeSeconds,
      this.state.velocity.length(),
      this.visualSurfaceRoughness,
      this.hoverMotionConfig(),
    );
    let h0 = this.prevHeading.value;
    let h1 = this.state.heading;
    const diff = h1 - h0;
    if (diff > Math.PI) h0 += Math.PI * 2;
    else if (diff < -Math.PI) h0 -= Math.PI * 2;
    const heading = h0 + (h1 - h0) * alpha;
    this.group.rotation.y = heading;
    this.updateVisualBank(h1 - h0, dt, timeSeconds);
    this.updateAccelPitch(dt);
    const bankLift = __test__computeBankClearanceLift(this.visualBank, this.modelScale);
    const bankCompression = __test__computeBankCompression(this.bankLoad, this.modelScale);
    this.updateTrackSuspension(groundY, dt);
    const buoyancyOffset = this.hoverBuoyancyAmplitude
      * Math.sin(timeSeconds * Math.PI * 2 * this.hoverBuoyancyHz);
    const speed = this.state.velocity.length();
    const speedLiftSignal = THREE.MathUtils.smoothstep(speed / 65, 0, 1);
    const speedLiftTarget = speedLiftSignal * this.hoverSpeedLift;
    const liftAlpha = this.hoverSpeedLiftResponse > 0
      ? 1 - Math.exp(-dt / this.hoverSpeedLiftResponse)
      : 1;
    this.smoothedSpeedLift += (speedLiftTarget - this.smoothedSpeedLift) * liftAlpha;
    this.group.position.y =
      groundY
      + HOVER_HEIGHT
      + hover.yOffset
      + bankLift
      - bankCompression
      + this.suspensionY
      + buoyancyOffset
      + this.smoothedSpeedLift;
    if (this.track) {
      const trackPos = locateTrackPosition(
        this.track,
        this.group.position.x,
        this.group.position.z,
      );
      const target = trackBankAtArc(this.track, trackPos.arcLength);
      // Light low-pass so the roll eases in/out instead of snapping at segment seams.
      this.surfaceBank += (target - this.surfaceBank) * 0.18;
    } else {
      this.surfaceBank = 0;
    }
    if (this.visualModelEnabled) {
      this.modelHolder.rotation.set(
        hover.pitch + this.visualPitch,
        THREE.MathUtils.degToRad(this.modelYawDeg),
        this.visualBank + this.surfaceBank,
      );
      this.modelHolder.scale.setScalar(this.modelScale * (1 + hover.pulse));
      this.updatePodAssembly(timeSeconds, dt);
    }
    this.forwardArrow.visible = this.visualModelEnabled && this.showForwardArrow;
  }

  getScrapeEmitters(target: ScrapeEmitter[] = []): ScrapeEmitter[] {
    const speed = this.state.velocity.length();
    const bankAbs = Math.abs(this.visualBank);
    const threshold = THREE.MathUtils.degToRad(this.scrapeBankThresholdDeg);
    if (speed < 18 || bankAbs <= threshold) {
      target.length = 0;
      return target;
    }

    const intensity = THREE.MathUtils.clamp((bankAbs - threshold) / THREE.MathUtils.degToRad(22), 0, 1)
      * THREE.MathUtils.clamp(speed / 55, 0, 1);
    const emitter = target[0] ?? { position: new THREE.Vector3(), intensity: 0 };
    const rightX = Math.cos(this.state.heading);
    const rightZ = -Math.sin(this.state.heading);
    const forwardX = Math.sin(this.state.heading);
    const forwardZ = Math.cos(this.state.heading);
    const lowSide = this.visualBank > 0 ? -1 : 1;
    const scrapeOffset = 1.55 * this.modelScale;
    emitter.position.set(
      this.state.position.x + rightX * lowSide * scrapeOffset - forwardX * 0.35,
      this.state.position.y + 0.064,
      this.state.position.z + rightZ * lowSide * scrapeOffset - forwardZ * 0.35,
    );
    emitter.intensity = intensity;
    target[0] = emitter;
    target.length = 1;
    return target;
  }

  /**
   * 0..1 ramp of the plume's "I'm firing" state, scaled by the rig's
   * `emitterIntensity` dial. Drives the heat-distortion post effect so
   * shimmer fades in/out with the cone instead of snapping on with raw input.
   */
  getPlumeIntensity(): number {
    return this.throttleHeldTime * this.podRigTuning.emitterIntensity * this.podRuntimeGlowScale;
  }

  /**
   * Six heat-distortion sample slots. We only fill the first two (one per
   * engine nozzle) — heat hazes the air *around* the engine, not the entire
   * cone trail. Tight radii keep the mask local.
   */
  getPlumeHeatPoints(target: Array<{ position: THREE.Vector3; radius: number }> = []): Array<{ position: THREE.Vector3; radius: number }> {
    if (!this.podAssembly) {
      target.length = 0;
      return target;
    }
    while (target.length < 6) target.push({ position: new THREE.Vector3(), radius: 0 });
    target.length = 6;
    const baseR = 0.05;
    plumeNozzleL.set(0, 0, 0);
    plumeNozzleR.set(0, 0, 0);
    this.podAssembly.coreL.mesh.getWorldPosition(plumeNozzleL);
    this.podAssembly.coreR.mesh.getWorldPosition(plumeNozzleR);
    target[0].position.copy(plumeNozzleL);
    target[0].radius = baseR;
    target[1].position.copy(plumeNozzleR);
    target[1].radius = baseR;
    target[2].radius = 0;
    target[3].radius = 0;
    target[4].radius = 0;
    target[5].radius = 0;
    return target;
  }

  getEngineNozzleWorldPositions(target: THREE.Vector3[] = []): THREE.Vector3[] {
    if (this.podAssembly) {
      const left = target[0] ?? new THREE.Vector3();
      const right = target[1] ?? new THREE.Vector3();
      this.podAssembly.nozzleL.getWorldPosition(left);
      this.podAssembly.nozzleR.getWorldPosition(right);
      target[0] = left;
      target[1] = right;
      target.length = 2;
      return target;
    }

    const sinYaw = Math.sin(this.state.heading);
    const cosYaw = Math.cos(this.state.heading);
    const sinRoll = Math.sin(this.visualBank);
    const cosRoll = Math.cos(this.visualBank);
    for (let i = 0; i < this.engineNozzleOffsets.length; i++) {
      const offset = this.engineNozzleOffsets[i];
      const scaledX = offset.x * this.modelScale;
      const scaledY = offset.y * this.modelScale;
      const scaledZ = offset.z * this.modelScale;
      const rolledX = scaledX * cosRoll - scaledY * sinRoll;
      const rolledY = scaledX * sinRoll + scaledY * cosRoll;
      const world = target[i] ?? new THREE.Vector3();
      world.set(
        this.state.position.x + rolledX * cosYaw + scaledZ * sinYaw,
        this.state.position.y + rolledY,
        this.state.position.z - rolledX * sinYaw + scaledZ * cosYaw,
      );
      target[i] = world;
    }
    target.length = this.engineNozzleOffsets.length;
    return target;
  }

  applyCockpitWhipPhysics(dt: number): void {
    if (!this.podAssembly) return;
    if (this.cockpitWhipPhysicsGain === 0 || !this.podRigTuning.couplingEnabled) return;
    if (!Number.isFinite(dt) || dt <= 0) return;

    // Drag the chassis in the direction of the whip. RIGHT vector matches
    // the Physics.ts convention: (cos h, 0, -sin h).
    const heading = this.state.heading;
    const pull = this.podAssembly.cockpitOffset.x * this.cockpitWhipPhysicsGain * dt;
    this.state.velocity.x += Math.cos(heading) * pull;
    this.state.velocity.z += -Math.sin(heading) * pull;
  }

  private updatePodAssembly(timeSeconds: number, frameDt: number = FIXED_PHYSICS_DT): void {
    if (!this.podAssembly) return;

    const heading = this.state.heading;
    const speed = this.state.velocity.length();
    const forwardSpeed = this.state.velocity.x * Math.sin(heading) + this.state.velocity.z * Math.cos(heading);
    const dt = Math.min(Math.max(frameDt, 0), 0.25);
    const jerk = THREE.MathUtils.clamp((forwardSpeed - this.prevForwardSpeed) / FIXED_PHYSICS_DT / 42, -1, 1);
    this.podAssembly.previousForwardSpeed = forwardSpeed;

    const turnLoad = THREE.MathUtils.clamp(this.bankSignal + this.bankLoad * Math.sign(this.bankSignal) * 0.35, -1, 1);
    const speedSignal = __test__computeBankSpeedSignal(speed);
    const cockpitAccelWorld = coupledAccel.copy(this.state.velocity).sub(this.prevVelocity).multiplyScalar(1 / FIXED_PHYSICS_DT);
    worldVectorToAssemblyLocal(cockpitAccelWorld, Math.sin(heading), Math.cos(heading), coupledAccelLocal);
    this.updateCockpitWhip(this.podAssembly, coupledAccelLocal, dt);
    this.updateEngineSpring(this.podAssembly, turnLoad, jerk, speedSignal, timeSeconds, coupledAccelLocal, dt);

    updateCableTube(
      this.podAssembly.cableL,
      cockpitAnchorForSide(-1, 0, this.podRigTuning).add(this.podAssembly.cockpitOffset),
      cockpitSocketDirection(-1, this.podRigTuning),
      engineAnchor(this.podAssembly.engineL.group, -1, 0, this.podRigTuning),
      engineSocketDirection(-1, this.podRigTuning),
      this.podRigTuning.cableSag,
      this.podAssembly.engineL.cableRestLength,
      this.podRigTuning.cableInsert,
      timeSeconds,
      this.podAssembly.engineL.seed,
      this.podRigTuning,
    );
    updateCableTube(
      this.podAssembly.cableR,
      cockpitAnchorForSide(1, 0, this.podRigTuning).add(this.podAssembly.cockpitOffset),
      cockpitSocketDirection(1, this.podRigTuning),
      engineAnchor(this.podAssembly.engineR.group, 1, 0, this.podRigTuning),
      engineSocketDirection(1, this.podRigTuning),
      this.podRigTuning.cableSag,
      this.podAssembly.engineR.cableRestLength,
      this.podRigTuning.cableInsert,
      timeSeconds,
      this.podAssembly.engineR.seed,
      this.podRigTuning,
    );
    updateCableTube(
      this.podAssembly.cableLAlt,
      cockpitAnchorForSide(-1, 1, this.podRigTuning).add(this.podAssembly.cockpitOffset),
      cockpitSocketDirection(-1, this.podRigTuning),
      engineAnchor(this.podAssembly.engineL.group, -1, 1, this.podRigTuning),
      engineSocketDirection(-1, this.podRigTuning),
      this.podRigTuning.cableSag * 0.72,
      this.podAssembly.engineL.cableAltRestLength,
      this.podRigTuning.cableInsert,
      timeSeconds,
      this.podAssembly.engineL.seed + 2.1,
      this.podRigTuning,
    );
    updateCableTube(
      this.podAssembly.cableRAlt,
      cockpitAnchorForSide(1, 1, this.podRigTuning).add(this.podAssembly.cockpitOffset),
      cockpitSocketDirection(1, this.podRigTuning),
      engineAnchor(this.podAssembly.engineR.group, 1, 1, this.podRigTuning),
      engineSocketDirection(1, this.podRigTuning),
      this.podRigTuning.cableSag * 0.72,
      this.podAssembly.engineR.cableAltRestLength,
      this.podRigTuning.cableInsert,
      timeSeconds,
      this.podAssembly.engineR.seed + 2.1,
      this.podRigTuning,
    );
    for (const strand of this.podAssembly.plasmaStrands) {
      updatePlasmaStrand(
        strand.tube,
        this.podAssembly.engineL.group.position,
        this.podAssembly.engineR.group.position,
        timeSeconds,
        speedSignal,
        this.podPreviewThrottle,
        strand.lane,
        strand.phase,
      );
    }

    // Throttle ramp: 0 = idle pilot flame, 1 = full plume. Snappy: ~0.25s on
    // press, ~0.3s release tail. Reused for mesh glow, heat, and actual lights.
    const ramp = dt;
    const target = THREE.MathUtils.clamp(this.podPreviewThrottle, 0, 1);
    const upRate = ramp / 0.25;
    const downRate = ramp / 0.3;
    if (target > this.throttleHeldTime) this.throttleHeldTime = Math.min(target, this.throttleHeldTime + upRate);
    else this.throttleHeldTime = Math.max(target, this.throttleHeldTime - downRate);

    const thrustRamp = this.throttleHeldTime;
    const glowScale = this.podRuntimeGlowScale;
    const throttleGlow = thrustRamp * 3.35;
    const glow = 0.45 + speedSignal * 1.1 + throttleGlow + Math.max(0, jerk) * 0.6 + Math.sin(timeSeconds * 17) * 0.14;
    const exhaustLight = Math.max(0, glow) * this.podRigTuning.engineGlowIntensity * glowScale;
    const reflectedLight = (0.2 + thrustRamp * 1.8 + speedSignal * 0.28) * this.podRigTuning.blueGlowIntensity * glowScale;
    this.podAssembly.lightL.intensity = exhaustLight;
    this.podAssembly.lightR.intensity = exhaustLight;
    this.podAssembly.lightL.distance = THREE.MathUtils.lerp(this.podRigTuning.engineGlowRange * 0.42, this.podRigTuning.engineGlowRange, thrustRamp);
    this.podAssembly.lightR.distance = this.podAssembly.lightL.distance;
    this.podAssembly.blueLightL.intensity = reflectedLight;
    this.podAssembly.blueLightR.intensity = reflectedLight;
    this.podAssembly.blueLightL.distance = THREE.MathUtils.lerp(this.podRigTuning.blueGlowRange * 0.55, this.podRigTuning.blueGlowRange, thrustRamp);
    this.podAssembly.blueLightR.distance = this.podAssembly.blueLightL.distance;
    const sourcePulse = 1 + Math.sin(timeSeconds * (8 + thrustRamp * 9)) * (0.035 + thrustRamp * 0.055);
    this.podAssembly.exhaustL.scale.setScalar(this.podRigTuning.engineNozzleScale * sourcePulse);
    this.podAssembly.exhaustR.scale.setScalar(this.podRigTuning.engineNozzleScale * sourcePulse);
    const opacity = THREE.MathUtils.clamp(
      (0.26 + thrustRamp * 0.44 + Math.sin(timeSeconds * 13) * 0.028) * (0.42 + glowScale * 0.58),
      0.16,
      0.78,
    );
    (this.podAssembly.exhaustL.material as THREE.MeshBasicMaterial).opacity = opacity;
    (this.podAssembly.exhaustR.material as THREE.MeshBasicMaterial).opacity = opacity;

    // Drive shader-based core cones + plasma strands.
    const flicker = 0.85 + Math.sin(timeSeconds * 19.0) * 0.08 + Math.sin(timeSeconds * 41.0) * 0.04;
    const idleIntensity = this.podRigTuning.idleIntensity * glowScale;
    const fullIntensity = Math.max(idleIntensity, this.podRigTuning.emitterIntensity * glowScale);
    const coreIntensity = THREE.MathUtils.lerp(idleIntensity, fullIntensity, this.throttleHeldTime) * flicker;
    // Idle plume covers the throat (~0.18 of the cone); full extends through
    // the geometry tip. Ramps with held thrust.
    const extension = THREE.MathUtils.lerp(0.18, 1.0, this.throttleHeldTime);
    this.podAssembly.coreL.setIntensity(coreIntensity);
    this.podAssembly.coreR.setIntensity(coreIntensity);
    this.podAssembly.coreL.setExtension(extension);
    this.podAssembly.coreR.setExtension(extension);
    this.podAssembly.coreL.update(dt);
    this.podAssembly.coreR.update(dt);
    const plasmaBaseIntensity = THREE.MathUtils.clamp(
      (0.78 + speedSignal * 0.18) * this.podRigTuning.plasmaIntensity * glowScale,
      0,
      2,
    );
    const plasmaMotion = 1 + this.podPreviewThrottle * 1.45 + speedSignal * 0.65;
    for (const strand of this.podAssembly.plasmaStrands) {
      const flicker =
        0.86
        + Math.sin(timeSeconds * (8.5 + plasmaMotion * 2.2) + strand.phase) * 0.08
        + Math.sin(timeSeconds * (24 + strand.phase) + strand.lane * 2.4) * 0.045;
      strand.material.setIntensity(plasmaBaseIntensity * strand.intensityScale * flicker);
      strand.material.advance(dt * (0.82 + plasmaMotion * 0.28 + strand.phase * 0.015));
    }
    if (this.podRigTuning.couplingEnabled) {
      const netPull = this.podAssembly.engineL.cableForce.x + this.podAssembly.engineR.cableForce.x;
      const netLift = this.podAssembly.engineL.cableForce.y + this.podAssembly.engineR.cableForce.y;
      this.modelHolder.rotation.z += netPull * this.podRigTuning.cockpitReactionRoll;
      this.modelHolder.rotation.x += netLift * this.podRigTuning.cockpitReactionPitch;
    }
    this.podAssembly.prevCockpitVelocity.copy(this.state.velocity);
    this.applyPodSelfLightingVisibility();
  }

  private updateEngineSpring(
    assembly: PodRacerAssembly,
    turnLoad: number,
    jerk: number,
    speedSignal: number,
    timeSeconds: number,
    cockpitAccelLocal: THREE.Vector3,
    dt: number,
  ): void {
    if (!this.podRigTuning.couplingEnabled) {
      this.updateLegacyEngineSpring(assembly.engineL, turnLoad, jerk, speedSignal, timeSeconds, dt);
      this.updateLegacyEngineSpring(assembly.engineR, turnLoad, jerk, speedSignal, timeSeconds, dt);
      return;
    }

    this.updateCoupledEngine(assembly.engineL, assembly.engineR, assembly, cockpitAccelLocal, turnLoad, speedSignal, timeSeconds, dt);
    this.updateCoupledEngine(assembly.engineR, assembly.engineL, assembly, cockpitAccelLocal, turnLoad, speedSignal, timeSeconds, dt);
  }

  private updateCockpitWhip(
    assembly: PodRacerAssembly,
    cockpitAccelLocal: THREE.Vector3,
    dt: number,
  ): void {
    const tuning = this.podRigTuning;
    const max = Math.max(0.01, tuning.cockpitWhipMaxOffset);
    // Soft-cap via tanh so the whip eases into the limit instead of
    // slamming into a clamp wall. The shape is identity-like for small
    // offsets and asymptotes to ±max — no visible "invisible wall" feel
    // when whipStrength is high or you yank the wheel hard. We shape
    // both the target and the integrated offset so the spring sees the
    // same value it just produced (no oscillation against the cap).
    const softCap = (v: number): number => max * Math.tanh(v / max);
    const targetX = tuning.couplingEnabled
      ? softCap(-cockpitAccelLocal.x * tuning.cockpitWhipStrength)
      : 0;
    assembly.cockpitVelocity.x += (targetX - assembly.cockpitOffset.x) * tuning.cockpitWhipStiffness * dt;
    assembly.cockpitVelocity.x *= Math.max(0, 1 - tuning.cockpitWhipDamping * dt);
    assembly.cockpitOffset.x += assembly.cockpitVelocity.x * dt;
    // Soft-cap the integrated position too. Hard safety clamp at 2×max
    // protects against pathological NaN/runaway scenarios but should
    // never trigger in normal play because the soft-cap saturates first.
    assembly.cockpitOffset.x = softCap(assembly.cockpitOffset.x);
    assembly.cockpitOffset.x = THREE.MathUtils.clamp(
      assembly.cockpitOffset.x,
      -max * 2,
      max * 2,
    );
    if (!Number.isFinite(assembly.cockpitOffset.x + assembly.cockpitVelocity.x)) {
      assembly.cockpitOffset.set(0, 0, 0);
      assembly.cockpitVelocity.set(0, 0, 0);
    }
    this.applyCockpitWhipVisuals();
  }

  private updateLegacyEngineSpring(
    engine: EngineSpringState,
    turnLoad: number,
    jerk: number,
    speedSignal: number,
    timeSeconds: number,
    dt: number,
  ): void {
    const verticalNoise = (
      Math.sin(timeSeconds * 4.7 + engine.seed) * 0.055
      + Math.sin(timeSeconds * 8.9 + engine.seed * 1.9) * 0.026
    ) * (0.35 + speedSignal * 0.9);
    const lateralTarget = engine.side * Math.max(0, turnLoad * engine.side) * (0.16 + speedSignal * 0.22);
    springDelta.set(
      lateralTarget,
      verticalNoise,
      -jerk * 0.18,
    );
    const omega = 9.2;
    const dampingRatio = 0.82;
    springDelta.sub(engine.offset);
    engine.velocity.addScaledVector(springDelta, omega * omega * dt);
    engine.velocity.addScaledVector(engine.velocity, -2 * dampingRatio * omega * dt);
    engine.offset.addScaledVector(engine.velocity, dt);
    engine.offset.x = THREE.MathUtils.clamp(engine.offset.x, -0.36, 0.36);
    engine.offset.y = THREE.MathUtils.clamp(engine.offset.y, -0.18, 0.22);
    engine.offset.z = THREE.MathUtils.clamp(engine.offset.z, -0.28, 0.2);
    engine.group.position.copy(engine.rest).add(engine.offset);
    engine.group.rotation.y = 0;
    engine.group.rotation.z = -engine.offset.x * 0.18;
    engine.group.rotation.x = engine.offset.y * 0.12 - jerk * 0.045;
    engine.cableForce.setScalar(0);
  }

  private updateCoupledEngine(
    engine: EngineSpringState,
    otherEngine: EngineSpringState,
    assembly: PodRacerAssembly,
    cockpitAccelLocal: THREE.Vector3,
    turnLoad: number,
    speedSignal: number,
    timeSeconds: number,
    dt: number,
  ): void {
    const tuning = this.podRigTuning;
    const enginePosition = coupledEnginePosition.copy(engine.rest).add(engine.offset);
    const cockpitAnchor = cockpitAnchorForSide(engine.side, 0, tuning).add(assembly.cockpitOffset);

    // Tick the slack envelope and compute the effective cable rest length.
    // While slack is active the cable is "longer" than nominal so the
    // engine plunges into the pod on impact and recovers visibly rather
    // than snapping straight back.
    if (engine.cableSlackTimer > 0) {
      engine.cableSlackTimer = Math.max(0, engine.cableSlackTimer - dt);
      if (engine.cableSlackTimer <= 0) engine.cableSlackStrength = 0;
    }
    const slackEnvelope = engine.cableSlackTotalSec > 0
      ? Math.max(0, engine.cableSlackTimer / engine.cableSlackTotalSec)
      : 0;
    const slackEased = slackEnvelope * slackEnvelope * (3 - 2 * slackEnvelope);
    // Reducing rest length during slack means the cable is "happy" at a
    // compressed pose — the engine can plunge toward the pod without the
    // cable fighting it back out, then nominal rest length recovers as
    // slack decays so the engine is gently pulled back to its rest pose.
    const effectiveRestLength = engine.cableRestLength
      * (1 - engine.cableSlackStrength * slackEased * COLLISION_CABLE_SLACK_DROP);

    coupledForce.set(0, 0, 0);
    const cableLength = computeCableForce(
      cockpitAnchor,
      enginePosition,
      engine.velocity,
      effectiveRestLength,
      tuning.cableStiffness,
      tuning.cableDamping,
      engine.cableForce,
    );
    coupledForce.add(engine.cableForce);

    if (tuning.inertialLagEnabled) {
      coupledForce.addScaledVector(cockpitAccelLocal, -tuning.inertiaMass);
    }

    const otherPosition = coupledOtherPosition.copy(otherEngine.rest).add(otherEngine.offset);
    coupledStrutVec.copy(otherPosition).sub(enginePosition);
    const strutLength = coupledStrutVec.length();
    if (strutLength > 0.0001) {
      coupledStrutDir.copy(coupledStrutVec).multiplyScalar(1 / strutLength);
      const strutStretch = strutLength - assembly.strutRestLength;
      const relativeSpeed = otherEngine.velocity.dot(coupledStrutDir) - engine.velocity.dot(coupledStrutDir);
      const strutMagnitude = tuning.strutStiffness * strutStretch + tuning.strutDamping * relativeSpeed;
      coupledForce.addScaledVector(coupledStrutDir, strutMagnitude);
    }

    coupledForce.addScaledVector(engine.offset, -tuning.restStiffness);

    const verticalNoise = (
      Math.sin(timeSeconds * 4.7 + engine.seed) * 0.055
      + Math.sin(timeSeconds * 8.9 + engine.seed * 1.9) * 0.026
    ) * (0.35 + speedSignal * 0.9);
    const lateralTarget = engine.side * Math.max(0, turnLoad * engine.side) * (0.16 + speedSignal * 0.22);
    coupledForce.x += lateralTarget * 0.3 * tuning.restStiffness;
    coupledForce.y += verticalNoise * 0.3 * tuning.restStiffness;

    engine.velocity.addScaledVector(coupledForce, dt);
    engine.velocity.multiplyScalar(Math.max(0, 1 - tuning.engineDamping * dt));
    engine.offset.addScaledVector(engine.velocity, dt);
    engine.offset.x = THREE.MathUtils.clamp(engine.offset.x, -0.36, 0.36);
    engine.offset.y = THREE.MathUtils.clamp(engine.offset.y, -0.18, 0.22);
    engine.offset.z = THREE.MathUtils.clamp(engine.offset.z, -0.28, 0.2);

    if (!Number.isFinite(engine.offset.x + engine.offset.y + engine.offset.z)) {
      engine.offset.set(0, 0, 0);
      engine.velocity.set(0, 0, 0);
    }

    engine.group.position.copy(engine.rest).add(engine.offset);
    const cableYaw = cableLength > 0.0001
      ? Math.atan2(cockpitAnchor.x - engine.group.position.x, cockpitAnchor.z - engine.group.position.z) * tuning.engineYawGain
      : 0;
    const clampedYaw = THREE.MathUtils.clamp(cableYaw, -0.18, 0.18);
    const yawAlpha = 1 - Math.exp(-tuning.engineYawSmooth * dt);
    if (engine.side < 0) {
      assembly.targetEngineYawL = THREE.MathUtils.lerp(assembly.targetEngineYawL, clampedYaw, yawAlpha);
      engine.group.rotation.y = assembly.targetEngineYawL;
    } else {
      assembly.targetEngineYawR = THREE.MathUtils.lerp(assembly.targetEngineYawR, clampedYaw, yawAlpha);
      engine.group.rotation.y = assembly.targetEngineYawR;
    }
    engine.group.rotation.z = -engine.offset.x * 0.18;
    engine.group.rotation.x = engine.offset.y * 0.12;
  }

  private applyPodRigTuning(): void {
    if (!this.podAssembly) return;
    const tuning = this.podRigTuning;
    this.modelScale = tuning.modelScale;
    this.applyCockpitWhipVisuals();
    this.podAssembly.pilot.scale.setScalar(tuning.pilotScale);
    this.podAssembly.engineL.rest.set(-tuning.engineSpread, tuning.engineHeight, tuning.engineForward);
    this.podAssembly.engineR.rest.set(tuning.engineSpread, tuning.engineHeight, tuning.engineForward);
    this.podAssembly.engineL.group.scale.setScalar(tuning.engineScale);
    this.podAssembly.engineR.group.scale.setScalar(tuning.engineScale);
    this.refreshPodConstraintRestLengths();
    this.podAssembly.engineL.group.position.copy(this.podAssembly.engineL.rest).add(this.podAssembly.engineL.offset);
    this.podAssembly.engineR.group.position.copy(this.podAssembly.engineR.rest).add(this.podAssembly.engineR.offset);
    const engineColor = colorFromHue(tuning.engineGlowHue);
    const blueColor = colorFromHue(tuning.blueGlowHue);
    this.podAssembly.lightL.color.copy(engineColor);
    this.podAssembly.lightR.color.copy(engineColor);
    this.podAssembly.blueLightL.color.copy(blueColor);
    this.podAssembly.blueLightR.color.copy(blueColor);
    for (const material of [
      this.podAssembly.exhaustL.material,
      this.podAssembly.exhaustR.material,
    ] as THREE.MeshBasicMaterial[]) {
      material.color.copy(engineColor);
    }
    // Emitter origin: nozzle anchor + per-side mirrored offset from rig sliders.
    const baseY = tuning.engineNozzleY + tuning.emitterOffsetY;
    const baseZ = tuning.engineNozzleZ + tuning.emitterOffsetZ;
    const offsetX = tuning.emitterOffsetX;
    this.podAssembly.exhaustL.position.set(-offsetX, baseY, baseZ);
    this.podAssembly.exhaustR.position.set(offsetX, baseY, baseZ);
    this.podAssembly.exhaustL.scale.setScalar(tuning.engineNozzleScale);
    this.podAssembly.exhaustR.scale.setScalar(tuning.engineNozzleScale);
    this.podAssembly.nozzleL.position.copy(this.podAssembly.exhaustL.position);
    this.podAssembly.nozzleR.position.copy(this.podAssembly.exhaustR.position);
    this.podAssembly.coreL.mesh.position.copy(this.podAssembly.exhaustL.position);
    this.podAssembly.coreR.mesh.position.copy(this.podAssembly.exhaustR.position);
    this.podAssembly.coreL.mesh.scale.setScalar(tuning.emitterScale);
    this.podAssembly.coreR.mesh.scale.setScalar(tuning.emitterScale);
    this.podAssembly.lightL.position.copy(this.podAssembly.exhaustL.position);
    this.podAssembly.lightR.position.copy(this.podAssembly.exhaustR.position);
    this.podAssembly.blueLightL.position.set(-offsetX, baseY, baseZ + 0.05);
    this.podAssembly.blueLightR.position.set(offsetX, baseY, baseZ + 0.05);
    for (const strand of this.podAssembly.plasmaStrands) {
      strand.material.setHue(tuning.plasmaHueDeg + strand.hueOffset);
      strand.material.setIntensity(tuning.plasmaIntensity * strand.intensityScale);
    }
    const coreHdr = hueToCoreHdr(tuning.coreHueDeg, true);
    const outerHdr = hueToCoreHdr(tuning.outerHueDeg, false);
    this.podAssembly.coreL.setCoreColor(coreHdr);
    this.podAssembly.coreR.setCoreColor(coreHdr);
    this.podAssembly.coreL.setOuterColor(outerHdr);
    this.podAssembly.coreR.setOuterColor(outerHdr);
    this.podAssembly.cableL.radius = tuning.cableRadius;
    this.podAssembly.cableR.radius = tuning.cableRadius;
    this.podAssembly.cableLAlt.radius = tuning.cableRadius * 0.78;
    this.podAssembly.cableRAlt.radius = tuning.cableRadius * 0.78;

    let connectorIndex = 0;
    for (const side of [-1, 1] as const) {
      for (const lane of [0, 1]) {
        this.podAssembly.cockpitConnectors[connectorIndex].rotation.set(
          THREE.MathUtils.degToRad(tuning.cockpitSocketPitchDeg),
          THREE.MathUtils.degToRad(tuning.cockpitSocketYawDeg * side),
          0,
        );
        this.podAssembly.cockpitConnectors[connectorIndex].scale.setScalar(tuning.connectorScale);
        this.podAssembly.engineConnectors[connectorIndex].position.copy(engineLocalAnchor(side, lane, tuning));
        this.podAssembly.engineConnectors[connectorIndex].rotation.set(
          THREE.MathUtils.degToRad(tuning.engineSocketPitchDeg),
          THREE.MathUtils.degToRad(-tuning.engineSocketYawDeg * side),
          0,
        );
        this.podAssembly.engineConnectors[connectorIndex].scale.setScalar(tuning.connectorScale / Math.max(0.001, tuning.engineScale));
        connectorIndex += 1;
      }
    }
    this.applyCockpitWhipVisuals();
    this.applyPodSelfLightingVisibility();
  }

  private applyCockpitWhipVisuals(): void {
    if (!this.podAssembly) return;
    const tuning = this.podRigTuning;
    const offset = this.podAssembly.cockpitOffset;
    this.podAssembly.cockpit.position.copy(this.podAssembly.cockpitRest).add(offset);
    this.podAssembly.cockpit.rotation.z = -offset.x * 0.16;
    this.podAssembly.pilot.position.set(offset.x, tuning.pilotY + offset.y, tuning.pilotZ + offset.z);
    let connectorIndex = 0;
    for (const side of [-1, 1] as const) {
      for (const lane of [0, 1]) {
        this.podAssembly.cockpitConnectors[connectorIndex].position
          .copy(cockpitAnchorForSide(side, lane, tuning))
          .add(offset);
        connectorIndex += 1;
      }
    }
  }

  private refreshPodConstraintRestLengths(): void {
    if (!this.podAssembly) return;
    const tuning = this.podRigTuning;
    this.podAssembly.engineL.cableRestLength = cockpitAnchorForSide(-1, 0, tuning)
      .distanceTo(engineRestAnchor(this.podAssembly.engineL, -1, 0, tuning));
    this.podAssembly.engineR.cableRestLength = cockpitAnchorForSide(1, 0, tuning)
      .distanceTo(engineRestAnchor(this.podAssembly.engineR, 1, 0, tuning));
    this.podAssembly.engineL.cableAltRestLength = cockpitAnchorForSide(-1, 1, tuning)
      .distanceTo(engineRestAnchor(this.podAssembly.engineL, -1, 1, tuning));
    this.podAssembly.engineR.cableAltRestLength = cockpitAnchorForSide(1, 1, tuning)
      .distanceTo(engineRestAnchor(this.podAssembly.engineR, 1, 1, tuning));
    this.podAssembly.strutRestLength = this.podAssembly.engineL.rest.distanceTo(this.podAssembly.engineR.rest);
  }

  private applyPodSelfLightingVisibility(): void {
    if (!this.podAssembly) return;
    const visible = this.podSelfLightingEnabled;
    this.podAssembly.exhaustL.visible = visible;
    this.podAssembly.exhaustR.visible = visible;
    this.podAssembly.coreL.mesh.visible = visible;
    this.podAssembly.coreR.mesh.visible = visible;
    for (const strand of this.podAssembly.plasmaStrands) {
      strand.tube.mesh.visible = visible;
    }
    this.podAssembly.lightL.visible = visible;
    this.podAssembly.lightR.visible = visible;
    this.podAssembly.blueLightL.visible = visible;
    this.podAssembly.blueLightR.visible = visible;
  }

  private updateVisualBank(headingDelta: number, dt: number, timeSeconds: number): void {
    if (dt <= 0) return;
    const heading = this.state.heading;
    const speed = this.state.velocity.length();
    const rightX = Math.cos(heading);
    const rightZ = -Math.sin(heading);
    const lateralSpeed = this.state.velocity.x * rightX + this.state.velocity.z * rightZ;
    const turnSignal = shapeBankSignal(headingDelta * 38, 0.2);
    const slipSignal = shapeBankSignal(lateralSpeed / 22, 0.16);
    const speedSignal = __test__computeBankSpeedSignal(speed);
    const rawTurn = THREE.MathUtils.clamp(turnSignal * 0.86 + slipSignal * 0.3, -1, 1);
    const signalAlpha = 1 - Math.exp(-(5.8 + speedSignal * 3.5) * dt);
    this.bankSignal += (rawTurn - this.bankSignal) * signalAlpha;
    let target = -this.bankSignal * speedSignal * THREE.MathUtils.degToRad(this.maxBankDeg);

    // Differential-thrust roll. Engine working harder lifts its side; body
    // rolls toward the slacker engine. Visible at zero speed (unlike the
    // turn-lean signal, which gates on speedSignal). Sign matches the
    // existing convention where +visualBank rolls toward world -X.
    if (this.engineThrustL !== undefined || this.engineThrustR !== undefined) {
      const l = this.engineThrustL ?? 0;
      const r = this.engineThrustR ?? 0;
      target += (l - r) * 0.5 * THREE.MathUtils.degToRad(this.thrustBankDeg);
    }
    // Ground-sample asymmetry. Sample the hover noise at the two engine
    // positions; the height delta gives an instantaneous slope estimate
    // that rolls the body naturally. On a flat track this is the noise
    // field alone (subtle idle rocking). When tracks gain elevation the
    // same code reads the slope for free without further changes.
    const groundDelta = this.sampleEngineGroundDelta(timeSeconds);
    target += groundDelta * THREE.MathUtils.degToRad(this.groundRollDeg);
    const omega = 9.5 + this.bankResponsiveness * 0.82 + speedSignal * 3.2;
    const dampingRatio = 1.32;
    this.bankVelocity += (
      (target - this.visualBank) * omega * omega
      - this.bankVelocity * 2 * dampingRatio * omega
    ) * dt;
    const nextBank = this.visualBank + this.bankVelocity * dt;
    if (
      Math.sign(target - this.visualBank) !== Math.sign(target - nextBank)
      && Math.abs(target - this.visualBank) < THREE.MathUtils.degToRad(4)
    ) {
      this.visualBank = target;
      this.bankVelocity = 0;
    } else {
      this.visualBank = nextBank;
    }
    const loadTarget = THREE.MathUtils.smoothstep(
      Math.abs(this.visualBank) / THREE.MathUtils.degToRad(Math.max(1, this.maxBankDeg)),
      0.32,
      0.9,
    ) * speedSignal;
    this.bankLoad += (loadTarget - this.bankLoad) * (1 - Math.exp(-7 * dt));
  }

  private updateAccelPitch(dt: number): void {
    if (dt <= 0) return;
    const heading = this.state.heading;
    const fwdX = Math.sin(heading);
    const fwdZ = Math.cos(heading);
    const vForward = this.state.velocity.x * fwdX + this.state.velocity.z * fwdZ;
    const speed = this.state.velocity.length();
    // Squat reads best when it engages right off the line. The bank signal
    // ramps in at ~14 m/s, which is too late — by then the racer is past the
    // hardest acceleration. Use a much tighter ramp here so launch pitch is
    // visible. Brake dive reaches max well above this band so it's unaffected.
    const speedSignal = THREE.MathUtils.smoothstep(speed, 2, 32);
    // Acceleration is a per-physics-step delta, so it must be normalized by
    // the fixed step (not the render dt) to read out as m/s².
    const accel = (vForward - this.prevForwardSpeed) / FIXED_PHYSICS_DT;
    const target = __test__computeAccelPitchTarget(
      accel,
      speedSignal,
      this.maxAccelPitchUpDeg,
      this.maxAccelPitchDownDeg,
      this.pitchAccelScale,
    );
    // No artificial "boost is active" pitch impulse. The visual squat
    // is driven purely by the actual acceleration — so a low-speed
    // boost (large accel) reads as a big squat, while a top-speed
    // boost (acceleration near zero, drag balances thrust at cap)
    // produces almost no squat. Honest to felt force, matching the
    // best-practice approach in Mario Kart, F-Zero, NFS, and Forza
    // (boost drama goes to engine plume + camera, body stays honest).
    const omega = this.pitchResponsiveness;
    const dampingRatio = 1.05;
    this.pitchVelocity += (
      (target - this.visualPitch) * omega * omega
      - this.pitchVelocity * 2 * dampingRatio * omega
    ) * dt;
    this.visualPitch += this.pitchVelocity * dt;
  }

  /**
   * Sample ground height under each engine and return the asymmetry as
   * a normalized [-1,+1] value (positive = right side higher → roll
   * left). Track is currently flat (y=0) so the delta is driven by a
   * cheap noise approximation tied to each engine's world position;
   * this is a placeholder for the full per-engine raycast that arrives
   * with elevated track sections. Hooked here so the visual rocking
   * shows up immediately and the wiring doesn't need a second pass.
   */
  private sampleEngineGroundDelta(timeSeconds: number): number {
    const heading = this.state.heading;
    const cosY = Math.cos(heading);
    const sinY = Math.sin(heading);
    // Engine offsets in body frame (mirror of engineNozzleOffsets x-axis).
    const offsetX = 1.45;
    const t = timeSeconds;
    const sampleAt = (bx: number): number => {
      const wx = this.state.position.x + bx * cosY + 0;
      const wz = this.state.position.z - bx * sinY + 0;
      const speed = this.state.velocity.length();
      // Two octaves of cheap trig noise. Phase ties to world position so
      // the racer reads each engine over different ground; rate ties to
      // speed so it gets bumpier the faster you go (within reason).
      const a = Math.sin(wx * 0.32 + t * (1.6 + speed * 0.04));
      const b = Math.sin(wz * 0.27 - t * (1.1 + speed * 0.03));
      return (a * 0.5 + b * 0.5) * this.visualSurfaceRoughness;
    };
    const left = sampleAt(-offsetX);
    const right = sampleAt(+offsetX);
    return THREE.MathUtils.clamp(right - left, -1, 1);
  }

  /**
   * Called by the game loop when the chassis touches down after being
   * airborne. The downward speed at touchdown gets translated into a
   * negative spike on the existing track-suspension spring — the chassis
   * compresses visually, then the critically-damped spring recovers it.
   * Below `landingImpactThreshold` we no-op (tiny micro-airs aren't worth
   * a visible thump).
   */
  applyLandingImpulse(downwardSpeed: number): void {
    if (downwardSpeed < this.landingImpactThreshold) return;
    this.suspensionVelocity -= downwardSpeed * this.landingCompressionGain;
    const minVel = -Math.max(0.5, this.trackSuspensionMaxOffset)
      * Math.max(0.5, this.trackSuspensionResponse) * 4;
    if (this.suspensionVelocity < minVel) this.suspensionVelocity = minVel;
  }

  /**
   * Track-following suspension: the chassis lags vertically when the ground
   * rises/falls under it. Driven by a critically-damped 1D spring whose
   * target is `-groundYVel * trackSuspensionTravel`. Net effect: the chassis
   * compresses (sits lower) on a climb, floats up on a descent, and settles
   * back to neutral on flats. Lower `trackSuspensionResponse` = slower,
   * smoother, weightier; higher = stiffer, more glued to the ground line.
   */
  private updateTrackSuspension(groundY: number, dt: number): void {
    if (dt <= 0) return;
    if (this.prevGroundY === null) {
      this.prevGroundY = groundY;
      return;
    }
    const groundYVel = (groundY - this.prevGroundY) / dt;
    this.prevGroundY = groundY;
    // Cap the input so a single physics-step teleport (respawn, collision pop)
    // can't slingshot the spring with a 1000 m/s impulse.
    const clampedVel = THREE.MathUtils.clamp(groundYVel, -8, 8);
    const target = -clampedVel * this.trackSuspensionTravel;
    const omega = Math.max(0.5, this.trackSuspensionResponse);
    const dampingRatio = 1.0;
    this.suspensionVelocity += (
      (target - this.suspensionY) * omega * omega
      - this.suspensionVelocity * 2 * dampingRatio * omega
    ) * dt;
    this.suspensionY += this.suspensionVelocity * dt;
    const maxOffset = Math.max(0, this.trackSuspensionMaxOffset);
    if (this.suspensionY > maxOffset) {
      this.suspensionY = maxOffset;
      if (this.suspensionVelocity > 0) this.suspensionVelocity = 0;
    } else if (this.suspensionY < -maxOffset) {
      this.suspensionY = -maxOffset;
      if (this.suspensionVelocity < 0) this.suspensionVelocity = 0;
    }
  }

  private hoverMotionConfig(): HoverMotionConfig {
    return {
      idleAmplitude: this.hoverIdleAmplitude,
      bounceAmplitude: this.hoverBounceAmplitude,
      vibrationAmplitude: this.hoverVibrationAmplitude,
      pitchDeg: this.hoverPitchDeg,
      pulseScale: this.enginePulseScale,
      highSpeedDamping: this.hoverHighSpeedDamping,
    };
  }

  private configureModelForShadows(root: THREE.Object3D): void {
    root.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) {
        const mesh = o as THREE.Mesh;
        // The pod GLBs are the densest meshes in the scene; casting them into
        // the shadow map costs hundreds of thousands of extra triangles per
        // frame. Grounding comes from ContactShadow, so keep these as receivers.
        mesh.castShadow = false;
        mesh.receiveShadow = true;
      }
    });
  }

  /**
   * Hover-pod placeholder facing +Z (engines forward, cockpit rear, exhaust at back).
   * Group convention: local +Z = direction of travel, so no rotation tricks here —
   * rotation.y = heading is applied by the parent Group at runtime.
   */
  private makePlaceholderModel(): THREE.Object3D {
    const root = new THREE.Group();

    // Twin engines: long cylinders at the FRONT, axis along +Z.
    const engineMat = new THREE.MeshStandardMaterial({
      color: 0xa86a32,
      roughness: 0.4,
      metalness: 0.7,
    });
    const engineGeom = new THREE.CylinderGeometry(0.55, 0.6, 2.6, 14);
    engineGeom.rotateX(Math.PI / 2); // align cylinder axis with Z

    const engineL = new THREE.Mesh(engineGeom, engineMat);
    engineL.position.set(-1.5, 0.55, 1.4);
    root.add(engineL);
    const engineR = engineL.clone();
    engineR.position.x = 1.5;
    root.add(engineR);

    // Bright emissive intake at the very FRONT of each engine — an unmistakable nose.
    const intakeMat = new THREE.MeshStandardMaterial({
      color: 0xffd066,
      emissive: 0xffaa33,
      emissiveIntensity: 1.4,
    });
    const intakeGeom = new THREE.ConeGeometry(0.4, 0.7, 12);
    intakeGeom.rotateX(Math.PI / 2);

    const intakeL = new THREE.Mesh(intakeGeom, intakeMat);
    intakeL.position.set(-1.5, 0.55, 3.0);
    root.add(intakeL);
    const intakeR = intakeL.clone();
    intakeR.position.x = 1.5;
    root.add(intakeR);

    // Cockpit/pod at the REAR — smaller, raised, central.
    const podMat = new THREE.MeshStandardMaterial({
      color: 0x4a2a18,
      roughness: 0.55,
      metalness: 0.35,
    });
    const pod = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.7, 1.6), podMat);
    pod.position.set(0, 0.55, -1.0);
    root.add(pod);

    // Canopy on top of pod — dark glass.
    const canopyMat = new THREE.MeshStandardMaterial({
      color: 0x10080a,
      roughness: 0.15,
      metalness: 0.9,
    });
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.35, 1.0), canopyMat);
    canopy.position.set(0, 1.05, -0.85);
    root.add(canopy);

    // Tethers — thin connecting beams from cockpit to engines.
    const tetherMat = new THREE.MeshStandardMaterial({
      color: 0x8a5a2a,
      roughness: 0.6,
    });
    const tetherGeom = new THREE.BoxGeometry(0.12, 0.12, 2.6);
    const tetherL = new THREE.Mesh(tetherGeom, tetherMat);
    tetherL.position.set(-0.85, 0.55, 0.2);
    tetherL.rotation.y = Math.atan2(-1.5 + 0.85, 1.4 + 1.0);
    root.add(tetherL);
    const tetherR = tetherL.clone();
    tetherR.position.x = 0.85;
    tetherR.rotation.y = -tetherL.rotation.y;
    root.add(tetherR);

    // Red tail/exhaust glow at the very BACK.
    const exhaustMat = new THREE.MeshBasicMaterial({ color: 0xff5522 });
    const exhaust = new THREE.Mesh(new THREE.SphereGeometry(0.18, 8, 6), exhaustMat);
    exhaust.position.set(0, 0.55, -1.95);
    root.add(exhaust);

    return root;
  }

}

const tubeTangent = new THREE.Vector3();
const tubeNormal = new THREE.Vector3();
const tubeBinormal = new THREE.Vector3();
const tubeDelta = new THREE.Vector3();
const tubeCenter = new THREE.Vector3();
const tubePrev = new THREE.Vector3();
const tubeNext = new THREE.Vector3();
const cableP0 = new THREE.Vector3();
const cableP1 = new THREE.Vector3();
const cableP2 = new THREE.Vector3();
const cableP3 = new THREE.Vector3();
const cableStartDir = new THREE.Vector3();
const cableEndDir = new THREE.Vector3();
const plasmaStart = new THREE.Vector3();
const plasmaEnd = new THREE.Vector3();
const plasmaAcross = new THREE.Vector3();
const plasmaSide = new THREE.Vector3();
const springDelta = new THREE.Vector3();
const plumeNozzleL = new THREE.Vector3();
const plumeNozzleR = new THREE.Vector3();
const coupledAccel = new THREE.Vector3();
const coupledAccelLocal = new THREE.Vector3();
const coupledEnginePosition = new THREE.Vector3();
const coupledOtherPosition = new THREE.Vector3();
const coupledForce = new THREE.Vector3();
const coupledCableVec = new THREE.Vector3();
const coupledCableDir = new THREE.Vector3();
const coupledStrutVec = new THREE.Vector3();
const coupledStrutDir = new THREE.Vector3();
const collisionImpulseWorld = new THREE.Vector3();
const collisionImpulseLocal = new THREE.Vector3();

function normalizePart(model: THREE.Object3D, targetLength: number): void {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const longest = Math.max(size.x, size.z, 0.001);
  model.scale.multiplyScalar(targetLength / longest);

  const fitted = new THREE.Box3().setFromObject(model);
  const center = fitted.getCenter(new THREE.Vector3());
  model.position.x -= center.x;
  model.position.z -= center.z;
  model.position.y -= fitted.min.y;
}

function createDynamicTube(
  name: string,
  radius: number,
  segments: number,
  radialSegments: number,
  material: THREE.Material,
): DynamicTube {
  const ringCount = segments + 1;
  const vertexCount = ringCount * radialSegments;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const uvs = new Float32Array(vertexCount * 2);
  const indices: number[] = [];

  for (let i = 0; i < ringCount; i++) {
    for (let j = 0; j < radialSegments; j++) {
      const vertex = i * radialSegments + j;
      uvs[vertex * 2] = i / segments;
      uvs[vertex * 2 + 1] = j / radialSegments;
      if (i < segments) {
        const a = i * radialSegments + j;
        const b = i * radialSegments + ((j + 1) % radialSegments);
        const c = (i + 1) * radialSegments + j;
        const d = (i + 1) * radialSegments + ((j + 1) % radialSegments);
        indices.push(a, c, b, b, c, d);
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setIndex(indices);
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3).setUsage(THREE.DynamicDrawUsage));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.frustumCulled = false;
  return { mesh, geometry, positions, normals, segments, radialSegments, radius };
}

function makeCableConnector(
  name: string,
  material: THREE.Material,
  side: -1 | 1,
): THREE.Group {
  const group = new THREE.Group();
  group.name = name;
  const sleeveGeom = new THREE.CylinderGeometry(0.068, 0.076, 0.28, 16);
  sleeveGeom.rotateX(Math.PI / 2);
  const sleeve = new THREE.Mesh(sleeveGeom, material);
  sleeve.name = `${name}-sleeve`;
  group.add(sleeve);

  const bootGeom = new THREE.CylinderGeometry(0.085, 0.047, 0.18, 16);
  bootGeom.rotateX(Math.PI / 2);
  const boot = new THREE.Mesh(bootGeom, material);
  boot.name = `${name}-cableBoot`;
  boot.position.z = 0.14;
  group.add(boot);

  const ringGeom = new THREE.TorusGeometry(0.082, 0.012, 6, 18);
  const ringA = new THREE.Mesh(ringGeom, material);
  const ringB = new THREE.Mesh(ringGeom, material);
  ringA.name = `${name}-clampA`;
  ringB.name = `${name}-clampB`;
  ringA.position.z = -0.09;
  ringB.position.z = 0.09;
  group.add(ringA, ringB);

  const boltGeom = new THREE.BoxGeometry(0.035, 0.05, 0.024);
  for (const y of [-0.07, 0.07]) {
    const bolt = new THREE.Mesh(boltGeom, material);
    bolt.name = `${name}-bolt`;
    bolt.position.set(0.082 * side, y, 0.01);
    group.add(bolt);
  }
  return group;
}

function colorFromHue(hue: number): THREE.Color {
  return new THREE.Color().setHSL(THREE.MathUtils.euclideanModulo(hue, 360) / 360, 0.95, 0.56);
}

function makeProceduralDetailTexture(baseHex: number, scratchHex: number): THREE.CanvasTexture | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const base = new THREE.Color(baseHex);
  const scratch = new THREE.Color(scratchHex);
  const image = ctx.createImageData(canvas.width, canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const idx = (y * canvas.width + x) * 4;
      const streak = Math.sin((x + y * 0.35) * 0.55) * 0.5 + 0.5;
      const speck = Math.random();
      const mix = THREE.MathUtils.clamp(streak * 0.18 + (speck > 0.93 ? 0.38 : 0), 0, 0.48);
      image.data[idx] = Math.round(THREE.MathUtils.lerp(base.r, scratch.r, mix) * 255);
      image.data[idx + 1] = Math.round(THREE.MathUtils.lerp(base.g, scratch.g, mix) * 255);
      image.data[idx + 2] = Math.round(THREE.MathUtils.lerp(base.b, scratch.b, mix) * 255);
      image.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 2);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function cockpitAnchorForSide(side: -1 | 1, lane = 0, tuning: PodRacerRigTuning): THREE.Vector3 {
  return tubeCenter.set(
    (tuning.cockpitSocketX - tuning.cockpitSocketDepth + lane * tuning.socketLaneGap) * side,
    tuning.cockpitSocketY + lane * tuning.socketLaneRise,
    tuning.cockpitSocketZ + lane * 0.08,
  ).clone();
}

function engineLocalAnchor(side: -1 | 1, lane = 0, tuning: PodRacerRigTuning): THREE.Vector3 {
  return tubeCenter.set(
    (-tuning.engineSocketX + tuning.engineSocketDepth - lane * tuning.socketLaneGap * 0.65) * side,
    tuning.engineSocketY + lane * tuning.socketLaneRise,
    tuning.engineSocketZ + lane * 0.04,
  ).clone();
}

function engineAnchor(
  engine: THREE.Group,
  side: -1 | 1,
  lane: number,
  tuning: PodRacerRigTuning,
): THREE.Vector3 {
  const local = engineLocalAnchor(side, lane, tuning).multiplyScalar(engine.scale.x);
  return tubeDelta.set(
    engine.position.x + local.x,
    engine.position.y + local.y,
    engine.position.z + local.z,
  ).clone();
}

function engineRestAnchor(
  engine: EngineSpringState,
  side: -1 | 1,
  lane: number,
  tuning: PodRacerRigTuning,
): THREE.Vector3 {
  const local = engineLocalAnchor(side, lane, tuning).multiplyScalar(engine.group.scale.x);
  return tubeDelta.set(
    engine.rest.x + local.x,
    engine.rest.y + local.y,
    engine.rest.z + local.z,
  ).clone();
}

function worldVectorToAssemblyLocal(
  world: THREE.Vector3,
  sinHeading: number,
  cosHeading: number,
  target: THREE.Vector3,
): THREE.Vector3 {
  return target.set(
    world.x * cosHeading - world.z * sinHeading,
    world.y,
    world.x * sinHeading + world.z * cosHeading,
  );
}

function computeCableForce(
  cockpitAnchor: THREE.Vector3,
  enginePosition: THREE.Vector3,
  engineVelocity: THREE.Vector3,
  restLength: number,
  stiffness: number,
  damping: number,
  targetForce: THREE.Vector3,
): number {
  coupledCableVec.copy(cockpitAnchor).sub(enginePosition);
  const length = coupledCableVec.length();
  targetForce.set(0, 0, 0);
  if (length <= 0.0001) return length;
  const stretch = length - restLength;
  if (stretch <= 0) return length;
  coupledCableDir.copy(coupledCableVec).multiplyScalar(1 / length);
  const velocityAlongCable = engineVelocity.dot(coupledCableDir);
  targetForce.copy(coupledCableDir).multiplyScalar(stiffness * stretch - damping * velocityAlongCable);
  return length;
}

function cockpitSocketDirection(side: -1 | 1, tuning: PodRacerRigTuning): THREE.Vector3 {
  return cableStartDir
    .set(0, 0, 1)
    .applyEuler(new THREE.Euler(
      THREE.MathUtils.degToRad(tuning.cockpitSocketPitchDeg),
      THREE.MathUtils.degToRad(tuning.cockpitSocketYawDeg * side),
      0,
      'XYZ',
    ))
    .normalize()
    .clone();
}

function engineSocketDirection(side: -1 | 1, tuning: PodRacerRigTuning): THREE.Vector3 {
  return cableEndDir
    .set(0, 0, 1)
    .applyEuler(new THREE.Euler(
      THREE.MathUtils.degToRad(tuning.engineSocketPitchDeg),
      THREE.MathUtils.degToRad(-tuning.engineSocketYawDeg * side),
      0,
      'XYZ',
    ))
    .normalize()
    .clone();
}

function updateCableTube(
  tube: DynamicTube,
  start: THREE.Vector3,
  startDirection: THREE.Vector3,
  end: THREE.Vector3,
  endDirection: THREE.Vector3,
  sag: number,
  restLength: number,
  insertDepth: number,
  timeSeconds: number,
  seed: number,
  tuning: PodRacerRigTuning,
): void {
  const currentLength = Math.max(0.0001, start.distanceTo(end));
  const safeRestLength = Math.max(0.0001, restLength);
  const slack = Math.max(0, safeRestLength - currentLength) / safeRestLength;
  const tension = Math.max(0, currentLength - safeRestLength) / safeRestLength;
  const sagScale = 1 + slack * tuning.cableSlackBoost;
  const weaveScale = Math.max(0, 1 - tension * tuning.cableWeaveDamping);
  const leadLength = Math.max(0.18, insertDepth + tube.radius * 4.5);
  cableP0.copy(start).addScaledVector(startDirection, -insertDepth);
  cableP1.copy(start).addScaledVector(startDirection, leadLength);
  cableP2.copy(end).addScaledVector(endDirection, -leadLength);
  cableP3.copy(end).addScaledVector(endDirection, insertDepth);
  updateTube(tube, (t, target) => {
    cubicBezier(cableP0, cableP1, cableP2, cableP3, t, target);
    target.y -= sag * sagScale * 4 * t * (1 - t);
    const weave = Math.sin(timeSeconds * 5.5 + seed + t * Math.PI * 2) * 0.018 * weaveScale * Math.sin(Math.PI * t);
    target.x += weave * (0.3 + Math.sin(Math.PI * t) * 0.7);
    return target;
  });
}

function cubicBezier(
  p0: THREE.Vector3,
  p1: THREE.Vector3,
  p2: THREE.Vector3,
  p3: THREE.Vector3,
  t: number,
  target: THREE.Vector3,
): THREE.Vector3 {
  const inv = 1 - t;
  const a = inv * inv * inv;
  const b = 3 * inv * inv * t;
  const c = 3 * inv * t * t;
  const d = t * t * t;
  return target.set(
    p0.x * a + p1.x * b + p2.x * c + p3.x * d,
    p0.y * a + p1.y * b + p2.y * c + p3.y * d,
    p0.z * a + p1.z * b + p2.z * c + p3.z * d,
  );
}

function updatePlasmaStrand(
  tube: DynamicTube,
  leftEngineCenter: THREE.Vector3,
  rightEngineCenter: THREE.Vector3,
  timeSeconds: number,
  speedSignal: number,
  throttle: number,
  lane: number,
  phase: number,
): void {
  plasmaStart.set(leftEngineCenter.x + 0.18, leftEngineCenter.y + 0.1, leftEngineCenter.z + 0.18);
  plasmaEnd.set(rightEngineCenter.x - 0.18, rightEngineCenter.y + 0.1, rightEngineCenter.z + 0.18);
  plasmaAcross.subVectors(plasmaEnd, plasmaStart).normalize();
  plasmaSide.crossVectors(THREE.Object3D.DEFAULT_UP, plasmaAcross);
  if (plasmaSide.lengthSq() < 0.0001) plasmaSide.set(0, 0, 1);
  plasmaSide.normalize();
  const laneOffset = lane * 0.055;
  const amplitude = 0.014 + speedSignal * 0.015 + throttle * 0.012;
  const pulse = timeSeconds * (9 + throttle * 8 + speedSignal * 5);
  updateTube(tube, (t, target) => {
    target.lerpVectors(plasmaStart, plasmaEnd, t);
    const envelope = Math.sin(Math.PI * t);
    target.addScaledVector(plasmaSide, laneOffset + Math.sin(pulse + phase + t * 22.0) * amplitude * envelope);
    target.y += Math.sin(pulse * 0.74 + phase * 1.3 + t * 17.0) * amplitude * 0.75 * envelope;
    target.z += Math.sin(pulse * 0.56 + phase + t * 19.0) * amplitude * 0.35 * envelope;
    return target;
  });
}

function updateTube(
  tube: DynamicTube,
  pointAt: (t: number, target: THREE.Vector3) => THREE.Vector3,
): void {
  const { positions, normals, segments, radialSegments, radius } = tube;
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    pointAt(Math.max(0, t - 1 / segments), tubePrev);
    pointAt(Math.min(1, t + 1 / segments), tubeNext);
    pointAt(t, tubeCenter);
    tubeTangent.subVectors(tubeNext, tubePrev).normalize();
    tubeNormal.crossVectors(tubeTangent, THREE.Object3D.DEFAULT_UP);
    if (tubeNormal.lengthSq() < 0.0001) tubeNormal.set(1, 0, 0);
    tubeNormal.normalize();
    tubeBinormal.crossVectors(tubeTangent, tubeNormal).normalize();

    for (let j = 0; j < radialSegments; j++) {
      const angle = (j / radialSegments) * Math.PI * 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const vertex = i * radialSegments + j;
      const offset = tubeDelta
        .copy(tubeNormal)
        .multiplyScalar(cos * radius)
        .addScaledVector(tubeBinormal, sin * radius);
      positions[vertex * 3] = tubeCenter.x + offset.x;
      positions[vertex * 3 + 1] = tubeCenter.y + offset.y;
      positions[vertex * 3 + 2] = tubeCenter.z + offset.z;
      normals[vertex * 3] = offset.x / radius;
      normals[vertex * 3 + 1] = offset.y / radius;
      normals[vertex * 3 + 2] = offset.z / radius;
    }
  }
  const positionAttr = tube.geometry.getAttribute('position') as THREE.BufferAttribute;
  const normalAttr = tube.geometry.getAttribute('normal') as THREE.BufferAttribute;
  positionAttr.needsUpdate = true;
  normalAttr.needsUpdate = true;
  tube.geometry.computeBoundingSphere();
}

function fitToTargetLength(model: THREE.Object3D, targetLengthMeters: number): void {
  const box = new THREE.Box3().setFromObject(model);
  const size = box.getSize(new THREE.Vector3());
  const longest = Math.max(size.x, size.z);
  if (longest > 0.001) {
    const scale = targetLengthMeters / longest;
    model.scale.setScalar(scale);
  }
  const newBox = new THREE.Box3().setFromObject(model);
  const minY = newBox.min.y;
  model.position.y -= minY;
}

function shapeBankSignal(value: number, deadZone: number): number {
  const abs = Math.abs(THREE.MathUtils.clamp(value, -1, 1));
  const shaped = THREE.MathUtils.smoothstep(abs, deadZone, 1);
  return Math.sign(value) * shaped;
}

export function __test__computeHoverMotion(
  timeSeconds: number,
  speed: number,
  surfaceRoughness: number,
  config: HoverMotionConfig,
): { yOffset: number; pitch: number; pulse: number } {
  const speedSignal = THREE.MathUtils.smoothstep(speed / 65, 0.04, 1);
  const roughness = THREE.MathUtils.clamp(surfaceRoughness, 0, 1);
  const damp = THREE.MathUtils.clamp(config.highSpeedDamping ?? 1, 0, 1);
  const idle = Math.sin(timeSeconds * 4.1) * config.idleAmplitude;
  const bounce =
    (
      Math.sin(timeSeconds * (7.2 + speed * 0.035)) * 0.62
      + Math.sin(timeSeconds * (12.4 + speed * 0.025) + 1.7) * 0.38
    )
    * config.bounceAmplitude
    * (0.18 + speedSignal * 0.34 * damp + roughness * 0.72);
  const vibration = Math.sin(timeSeconds * (34 + speed * 0.22))
    * config.vibrationAmplitude
    * (0.2 + speedSignal * 0.35 * damp + roughness * 0.8);
  const pitch = Math.sin(timeSeconds * (5.8 + speed * 0.045) + 0.6)
    * THREE.MathUtils.degToRad(config.pitchDeg)
    * (0.18 + speedSignal * 0.4 * damp + roughness * 0.7);
  const pulse = Math.sin(timeSeconds * (18 + speed * 0.04))
    * config.pulseScale
    * (0.35 + speedSignal * 0.35 + roughness * 0.3);

  return {
    yOffset: idle + bounce + vibration,
    pitch,
    pulse,
  };
}

export function __test__computeBankClearanceLift(bankRadians: number, modelScale: number): number {
  const bank = Math.abs(bankRadians);
  const baseLift = Math.sin(bank) * 0.72 * modelScale;
  const hardBankLift = THREE.MathUtils.smoothstep(bank, THREE.MathUtils.degToRad(34), THREE.MathUtils.degToRad(58))
    * 0.18
    * modelScale;
  return baseLift + hardBankLift;
}

export function __test__computeBankCompression(bankLoad: number, modelScale: number): number {
  return THREE.MathUtils.clamp(bankLoad, 0, 1) * 0.11 * modelScale;
}

export function __test__computeBankSpeedSignal(speed: number): number {
  return THREE.MathUtils.smoothstep(speed, 14, 58);
}

/**
 * Convention: model faces +Z, so a +rotation.x tilts the nose down (dive)
 * and -rotation.x lifts the nose (squat).
 */
export function __test__computeAccelPitchTarget(
  accel: number,
  speedSignal: number,
  maxUpDeg: number,
  maxDownDeg: number,
  scale: number,
): number {
  if (scale <= 0) return 0;
  if (accel >= 0) {
    const n = THREE.MathUtils.clamp(accel / scale, 0, 1);
    return -THREE.MathUtils.degToRad(maxUpDeg) * n * speedSignal;
  }
  const n = THREE.MathUtils.clamp(-accel / scale, 0, 1);
  return THREE.MathUtils.degToRad(maxDownDeg) * n * speedSignal;
}
